import {
  add,
  compare,
  divide,
  format,
  fromNumber,
  fromString,
  multiply,
  serialize,
  subtract,
  toNumber,
} from './core/BigNumber.js';
import { WorldClock, formatDuration } from './core/WorldClock.js';
import { WorldScene } from './core/WorldScene.js';
import { cloudApi } from './core/Api.js';
import { sharedAuth } from './core/Auth.js';
import { AccountUI } from './ui/AccountUI.js';
import { RANKS } from './data/units.js';
import { SECTORS } from './data/sectors.js';
import { EVENT_REWARD_SECONDS, EVENT_WINDOW_MS, ICT_DURATION_MS, ICT_INTERVAL_MS, IPPT_INTERVAL_MS, ICT_OPERATION, MAX_EVENT_INTERVAL_MS, MIN_EVENT_INTERVAL_MS, OPERATIONS } from './data/operations.js';
import { SECTOR_COORDS } from './data/world.js';
import { VOCATIONS, unitsForVocation, vocationFor } from './data/vocations.js';
import { UPGRADES, upgradeFor } from './data/upgrades.js';
import { PILLARS, unlockedPillars } from './data/pillars.js';
import { phaseFor } from './data/lore.js';
import { commandFor, orderReachable } from './data/commands.js';
import { proficiencyMultiplier, nextProficiency, proficiencyLevel } from './data/proficiency.js';
import { ACHIEVEMENTS, evaluateAchievements, serviceRecordMultiplier } from './data/achievements.js';
import { CHALLENGES, challengeFor, challengeRewardMult } from './data/challenges.js';
import { COMMISSION_UPGRADES, COMMISSION_UNLOCK_COMMENDATIONS, commandPointGain, commissionMult, commissionFor } from './data/commission.js';
import { SEASONS, activeSeason, seasonMultiplier } from './data/seasons.js';
import { MARKET_BASE, MARKET_FEE, MARKET_POSITION_SECONDS, walkMarketPrice } from './data/market.js';
import { geometricCostSum, maxAffordable } from './core/economy.js';
import { morphInto } from './core/morph.js';
import { MODES, modeFor } from './modes/index.js';
import * as Fx from './core/Fx.js';
import './style.css';

const STORAGE_KEY = 'melodia.total-defence.m3';
const LEGACY_STORAGE_KEY = 'melodia.total-defence.m2';
const OLDER_STORAGE_KEY = 'melodia.total-defence.m1';
const GUEST_BACKUP_KEY = 'melodia.total-defence.guest-backup';
const OFFLINE_FACTOR = 0.5;
// Breath banks while you are away and converts to a short, heavy burst when you
// return and spend it — the mechanic that makes being present worth something
// without punishing the offline play that makes it an idler.
const SURGE_MULT = 3;
const BREATH_CAP_S = 3 * 60 * 60; // at most a few hours' worth banks up
const BREATH_MIN_S = 60; // need a minute banked before a burst is worth firing
const SURGE_MS_PER_BREATH_S = 40; // banked seconds → burst milliseconds
const MIN_SURGE_MS = 8_000;
const MAX_SURGE_MS = 120_000;
// Rare Signal — the golden-cookie clickable that blinks onto the theatre.
const SIGNAL_MIN_INTERVAL = 3 * 60 * 1000;
const SIGNAL_MAX_INTERVAL = 8 * 60 * 1000;
const SIGNAL_WINDOW = 20_000;
const SIGNAL_FRENZY_MS = 30_000;
// Late-game hands-off play. Gated on the highest rank ever reached, so once
// earned it survives ORD — a small standing-orders reward for prestiging.
const AUTO_BOOK_RANK = 5;
const AUTO_RESPOND_RANK = 8;
// How many observed price points the Stockpile Market sparkline keeps. Declared
// up here because loadState() reads it at module init, before the market
// helpers further down have been evaluated.
const MARKET_HISTORY_POINTS = 48;
// Rally — the active verb. Tapping the theatre pays a slice of your standing
// output and chains while you keep going, so being at the keyboard is a thing
// you *do* rather than a thing you watch.
const RALLY_SECONDS = 1.5;
const RALLY_CHAIN_WINDOW = 1800;
const RALLY_CHAIN_CAP = 25;

let offlineReport = null;
let selectedSectorId = 6;
let scene = null;
let activeMode = null;
let activeModeId = null;
let rallyChain = 0;
let rallyChainUntil = 0;
let lastRankSeen = 0;
// Latest world snapshot for the persistent WorldScene to read each frame.
let sceneSnapshot = null;
let inBackground = document.visibilityState !== 'visible';
let feed = [{ time: 'now', text: 'Field account opened. The regulars are already on duty.' }];
const auth = sharedAuth();
const cloud = cloudApi(auth);
let cloudMode = false;
let cloudAnnounced = false;
let achievementSyncPending = false;

function nextInterval(seed, index) {
  const value = Math.abs(Math.sin((seed + index * 17.17) * 12.9898) * 43758.5453) % 1;
  return MIN_EVENT_INTERVAL_MS + value * (MAX_EVENT_INTERVAL_MS - MIN_EVENT_INTERVAL_MS);
}

function initialState() {
  const now = Date.now();
  const seed = Math.floor((now % 1000000000) + 1);
  return {
    version: 3,
    vocation: 'army',
    credits: fromNumber(50),
    lifetime: fromNumber(0),
    units: { 1: 1 },
    posture: {},
    rankIndex: 0,
    rankRecord: 0,
    tutorialStep: 0,
    tutorialComplete: false,
    enlistmentNumber: 1,
    isNsman: false,
    commendations: 0,
    glimmer: 0,
    archive: [],
    achievements: [],
    activeChallenge: null,
    challengesDone: [],
    tempoLevel: 0,
    managers: [],
    commissions: 0,
    commandPoints: 0,
    commissionUpgrades: [],
    marketPrice: MARKET_BASE,
    marketHistory: [],
    marketLots: 0,
    signal: null,
    nextSignalAt: 0,
    signalUntil: 0,
    signalKind: null,
    signalMult: 1,
    modeBests: {},
    modeRuns: 0,
    rallies: 0,
    statistics: { eventsSeen: 0, eventsResponded: 0, eventsMissed: 0, unitsBooked: 1, upgradesBought: 0, ipptChecks: 0, ictCompleted: 0, ordersCompleted: 0, ords: 0, signalsCaught: 0 },
    upgrades: [],
    pillars: ['military'],
    commons: { groundHold: fromNumber(0), supply: fromNumber(0), readiness: fromNumber(0), intel: fromNumber(0) },
    ictOptIn: false,
    ictNextAt: now + ICT_INTERVAL_MS,
    ictStartedAt: 0,
    ictEndsAt: 0,
    ictClaimed: false,
    ipptNextAt: now + IPPT_INTERVAL_MS,
    ipptBonus: 0,
    ipptGrade: null,
    ipptExpiresAt: 0,
    ipptSteps: 0,
    pendingOverlay: null,
    pendingPillarId: null,
    lastTick: now,
    worldSeed: seed,
    worldClockRealOrigin: now,
    worldClockVirtualOrigin: now,
    eventIndex: 0,
    eventLastAt: now,
    nextEventAt: now + nextInterval(seed, 0),
    currentEvent: null,
    eventHistory: [],
    serverThreats: [],
    commandIndex: 0,
    visualEffects: {},
    breath: 0,
    surgeStartedAt: 0,
    surgeUntil: 0,
    autoBook: false,
    autoRespond: false,
  };
}

function loadState() {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY) || localStorage.getItem(OLDER_STORAGE_KEY));
  } catch {
    saved = null;
  }
  const base = initialState();
  if (!saved) return base;
  const state = {
    ...base,
    ...saved,
    version: 3,
    credits: saved.credits?.m !== undefined ? saved.credits : fromNumber(saved.credits || 0),
    lifetime: saved.lifetime?.m !== undefined ? saved.lifetime : fromNumber(saved.lifetime || 0),
    lastTick: Number(saved.lastTick) || base.lastTick,
    worldSeed: Number(saved.worldSeed) || base.worldSeed,
    worldClockRealOrigin: Number(saved.worldClockRealOrigin) || base.worldClockRealOrigin,
    worldClockVirtualOrigin: Number(saved.worldClockVirtualOrigin) || base.worldClockVirtualOrigin,
    eventIndex: Number.isFinite(saved.eventIndex) ? saved.eventIndex : (Number(saved.operationIndex) || 0),
    eventLastAt: Number(saved.eventLastAt) || base.eventLastAt,
    nextEventAt: Number(saved.nextEventAt) || base.nextEventAt,
    // A cloud threat is authoritative only while the cloud link is present.
    // If credentials disappear later, do not leave the local event loop paused
    // forever by a stale server marker.
    currentEvent: saved.currentEvent?.server && !(cloud.configured && cloud.accessToken) ? null : saved.currentEvent || null,
    eventHistory: Array.isArray(saved.eventHistory) ? saved.eventHistory : [],
    serverThreats: saved.currentEvent?.server && !(cloud.configured && cloud.accessToken) ? [] : (Array.isArray(saved.serverThreats) ? saved.serverThreats : []),
    commandIndex: Number.isFinite(saved.commandIndex) ? Math.max(0, Number(saved.commandIndex)) : 0,
    visualEffects: saved.visualEffects || {},
    rankRecord: Number(saved.rankRecord) || Number(saved.rankIndex) || base.rankRecord,
    tutorialStep: Number(saved.tutorialStep) || 0,
    tutorialComplete: saved.tutorialComplete === undefined ? true : Boolean(saved.tutorialComplete),
    enlistmentNumber: Number(saved.enlistmentNumber) || base.enlistmentNumber,
    isNsman: Boolean(saved.isNsman),
    commendations: Number(saved.commendations) || 0,
    glimmer: Number(saved.glimmer) || 0,
    archive: Array.isArray(saved.archive) ? saved.archive : [],
    achievements: Array.isArray(saved.achievements) ? saved.achievements : [],
    activeChallenge: saved.activeChallenge || null,
    challengesDone: Array.isArray(saved.challengesDone) ? saved.challengesDone : [],
    tempoLevel: Number(saved.tempoLevel) || 0,
    commissions: Number(saved.commissions) || 0,
    commandPoints: Number(saved.commandPoints) || 0,
    commissionUpgrades: Array.isArray(saved.commissionUpgrades) ? saved.commissionUpgrades : [],
    managers: Array.isArray(saved.managers) ? saved.managers.map(Number) : [],
    marketPrice: Number(saved.marketPrice) || MARKET_BASE,
    marketHistory: Array.isArray(saved.marketHistory)
      ? saved.marketHistory.map(Number).filter(Number.isFinite).slice(-MARKET_HISTORY_POINTS)
      : [],
    marketLots: Number(saved.marketLots) || 0,
    signal: saved.signal || null,
    nextSignalAt: Number(saved.nextSignalAt) || 0,
    signalUntil: Number(saved.signalUntil) || 0,
    signalKind: saved.signalKind || null,
    signalMult: Number(saved.signalMult) || 1,
    modeBests: saved.modeBests && typeof saved.modeBests === 'object' ? saved.modeBests : {},
    modeRuns: Number(saved.modeRuns) || 0,
    rallies: Number(saved.rallies) || 0,
    statistics: { ...base.statistics, ...(saved.statistics || {}) },
    upgrades: Array.isArray(saved.upgrades) ? saved.upgrades : [],
    pillars: Array.isArray(saved.pillars) && saved.pillars.length ? saved.pillars : ['military'],
    commons: Object.fromEntries(Object.keys(base.commons).map((key) => [key, saved.commons?.[key]?.m !== undefined ? saved.commons[key] : fromNumber(saved.commons?.[key] || 0)])),
    ictOptIn: Boolean(saved.ictOptIn),
    ictNextAt: Number(saved.ictNextAt) || base.ictNextAt,
    ictStartedAt: Number(saved.ictStartedAt) || 0,
    ictEndsAt: Number(saved.ictEndsAt) || 0,
    ictClaimed: Boolean(saved.ictClaimed),
    ipptNextAt: Number(saved.ipptNextAt) || base.ipptNextAt,
    ipptBonus: Number(saved.ipptBonus) || 0,
    ipptGrade: saved.ipptGrade || null,
    ipptExpiresAt: Number(saved.ipptExpiresAt) || 0,
    ipptSteps: Number(saved.ipptSteps) || 0,
    pendingOverlay: saved.pendingOverlay || null,
    breath: Math.max(0, Math.min(BREATH_CAP_S, Number(saved.breath) || 0)),
    surgeStartedAt: Number(saved.surgeStartedAt) || 0,
    surgeUntil: Number(saved.surgeUntil) || 0,
    autoBook: Boolean(saved.autoBook),
    autoRespond: Boolean(saved.autoRespond),
  };
  if (!VOCATIONS.some((vocation) => vocation.id === state.vocation)) state.vocation = 'army';
  state.pillars = unlockedPillars(state.rankIndex).map((pillar) => pillar.id);
  state.rankRecord = Math.max(state.rankRecord, state.rankIndex);
  if (!saved.nextEventAt) state.nextEventAt = state.eventLastAt + nextInterval(state.worldSeed, state.eventIndex);
  return state;
}

let state = loadState();
const clock = new WorldClock({ realOrigin: state.worldClockRealOrigin, virtualOrigin: state.worldClockVirtualOrigin });
let accountUI = null;

function saveState() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    ...state,
    credits: serialize(state.credits),
    lifetime: serialize(state.lifetime),
  }));
}

function preserveGuestSave() {
  if (cloudMode) return;
  const guestSave = localStorage.getItem(STORAGE_KEY);
  if (guestSave) localStorage.setItem(GUEST_BACKUP_KEY, guestSave);
}

function restoreGuestSave() {
  const guestSave = localStorage.getItem(GUEST_BACKUP_KEY);
  if (!guestSave) {
    state = initialState();
    return;
  }
  localStorage.setItem(STORAGE_KEY, guestSave);
  state = loadState();
  localStorage.removeItem(GUEST_BACKUP_KEY);
}

function cloudBig(value) {
  if (value?.m !== undefined) return value;
  if (typeof value === 'number') return fromNumber(value);
  if (typeof value === 'string' && value) return fromString(value);
  return fromNumber(0);
}

function cloudTime(value, fallback = 0) {
  const parsed = typeof value === 'number' ? value : Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mergeCloudPayload(payload) {
  const remote = payload?.save || payload;
  if (!remote?.user_id) return false;
  const remoteCommons = payload?.commons || {};
  const parsedPosture = payload?.posture && typeof payload.posture === 'object'
    ? Object.fromEntries(Object.entries(payload.posture).map(([id, pct]) => [id, Number(pct) || 0]))
    : state.posture;
  const serverThreats = Array.isArray(payload?.threats) ? payload.threats : [];
  state = {
    ...state,
    vocation: remote.vocation || state.vocation,
    credits: cloudBig(remote.credits),
    lifetime: cloudBig(remote.lifetime),
    units: remote.units || state.units,
    upgrades: remote.upgrades || state.upgrades,
    commendations: Number(remote.commendations) || 0,
    glimmer: Number(remote.glimmer) || state.glimmer,
    rankIndex: Number(remote.rank_index) || 0,
    rankRecord: Number(remote.rank_record) || 0,
    pillars: remote.pillars || state.pillars,
    commandIndex: Number.isFinite(remote.command_index) ? Math.max(0, Number(remote.command_index)) : state.commandIndex,
    posture: parsedPosture,
    isNsman: Boolean(remote.is_nsman),
    achievements: Array.isArray(remote.achievements) ? remote.achievements : state.achievements,
    archive: Array.isArray(remote.archive) ? remote.archive : state.archive,
    tempoLevel: Number(remote.tempo_level) || 0,
    activeChallenge: remote.active_challenge || null,
    challengesDone: Array.isArray(remote.challenges_done) ? remote.challenges_done : state.challengesDone,
    commissions: Number(remote.commissions) || 0,
    commandPoints: Number(remote.command_points) || 0,
    commissionUpgrades: Array.isArray(remote.commission_upgrades) ? remote.commission_upgrades : state.commissionUpgrades,
    managers: Array.isArray(remote.managers) ? remote.managers.map(Number) : state.managers,
    marketLots: Number(remote.market_lots) || 0,
    marketPrice: Number(payload?.market?.price) || state.marketPrice || MARKET_BASE,
    breath: Number(remote.breath) || 0,
    surgeStartedAt: cloudTime(remote.surge_started_at, 0),
    surgeUntil: cloudTime(remote.surge_ends_at, 0),
    autoBook: Boolean(remote.auto_book),
    autoRespond: Boolean(remote.auto_respond),
    ipptGrade: remote.ippt_grade || null,
    ipptExpiresAt: cloudTime(remote.ippt_expires, 0),
    ictNextAt: cloudTime(remote.ict_next_at, state.ictNextAt),
    ictStartedAt: cloudTime(remote.ict_started_at, 0),
    ictEndsAt: cloudTime(remote.ict_ends_at, 0),
    lastTick: cloudTime(remote.last_tick, state.lastTick),
    commons: {
      groundHold: cloudBig(remoteCommons.ground_hold),
      supply: cloudBig(remoteCommons.supply),
      readiness: cloudBig(remoteCommons.readiness),
      intel: cloudBig(remoteCommons.intel),
    },
    tutorialComplete: true,
    serverThreats,
  };
  cloudMode = true;
  const remoteThreat = serverThreats[0];
  const previousServerEvent = state.currentEvent?.server && state.currentEvent.serverId === remoteThreat?.id ? state.currentEvent : null;
  if (remoteThreat) {
    const operationIndex = OPERATIONS.findIndex((operation) => operation.sector === Number(remoteThreat.sector_id));
    state.currentEvent = {
      operationIndex: operationIndex >= 0 ? operationIndex : 0,
      server: true,
      serverId: remoteThreat.id,
      startedAt: cloudTime(remoteThreat.spawn_at, clock.now()),
      resolvesAt: cloudTime(remoteThreat.resolve_at, clock.now() + EVENT_WINDOW_MS),
      responded: Boolean(previousServerEvent?.responded),
      intelRevealedAt: cloudTime(remoteThreat.intel_revealed_at, clock.now()),
    };
  } else if (cloudMode) {
    state.currentEvent = null;
  }
  return true;
}

async function syncCloudState() {
  if (!cloud.configured || !cloud.accessToken) return;
  const wasCloudMode = cloudMode;
  let provisioned = false;
  let result = await cloud.state();
  if (result.error && /SAF save not found/i.test(result.error.message || '')) {
    result = await cloud.enlist(state.vocation);
    provisioned = !result.error;
  }
  const { data, error } = result;
  if (error || !mergeCloudPayload(data)) return;
  if (!wasCloudMode && !cloudAnnounced) {
    addFeed('Cloud command link established. Balance and force are now server-derived.');
    cloudAnnounced = true;
  }
  if (provisioned) addFeed('Total Defence save created. This watch is now linked to the shared identity.');
  saveState();
  render();
}

async function handleAuthSession(session) {
  if (session) {
    preserveGuestSave();
    await syncCloudState();
    accountUI?.render();
    return;
  }
  cloudMode = false;
  cloudAnnounced = false;
  restoreGuestSave();
  offlineReport = null;
  feed = [{ time: 'now', text: 'Guest watch reopened. The regulars are already on duty.' }];
  saveState();
  render();
}

async function syncCloudIntent(request, successText = '') {
  const { data, error } = await request;
  if (error || !mergeCloudPayload(data)) {
    addFeed(error?.message || 'Cloud command rejected. Local watch remains available.');
    render();
    return false;
  }
  if (successText) addFeed(successText);
  saveState();
  render();
  return true;
}

function currentRankIndex() {
  let index = 0;
  for (let rank = 0; rank < RANKS.length; rank += 1) {
    if (compare(state.lifetime, fromNumber(RANKS[rank].threshold)) >= 0) index = rank;
  }
  return index;
}

function activeUnits() { return unitsForVocation(state.vocation); }

// When a promotion brings a Total Defence pillar online, queue a celebration
// so the "you stop being a serviceman and start being the system" beat lands.
function notePillarUnlock(previousRank) {
  if (!state.tutorialComplete || state.pendingOverlay) return;
  const pillar = PILLARS.find((entry) => entry.id !== 'military' && entry.unlock > previousRank && entry.unlock <= state.rankIndex);
  if (!pillar) return;
  state.pendingOverlay = 'pillar';
  state.pendingPillarId = pillar.id;
}

function commonsLog(value) {
  if (!value || value.m === 0) return 0;
  if (value.e > 308) return 308;
  return Math.log10(Math.max(0, toNumber(value) / 1e6) + 1);
}

function serviceMultipliers() {
  const vocation = vocationFor(state.vocation);
  const supply = 1 + (0.5 * commonsLog(state.commons.supply));
  const readiness = 1 / (1 + (0.3 * commonsLog(state.commons.readiness)));
  const intelLeadHours = Math.min(24, 1 + (3 * commonsLog(state.commons.intel)));
  const maxActiveSectors = Math.min(10, 3 + Math.floor(Math.log10(Math.max(1, toNumber(state.commons.groundHold) / 1e5 + 1))));
  return { vocation, supply, readiness, intelLeadHours, maxActiveSectors };
}

function personalMultiplier() {
  return stableMultiplier() * temporaryMultiplierAt(Date.now());
}

const TEMPO_BASE_COST = 5000;
const TEMPO_GROWTH = 3;
function tempoMultiplier() { return 1.1 ** (Number(state.tempoLevel) || 0); }
function tempoCost() {
  const discount = hasCommission('tempo-cheap') ? 0.4 : 1;
  return fromNumber(TEMPO_BASE_COST * discount * (TEMPO_GROWTH ** (Number(state.tempoLevel) || 0)));
}

function hasCommission(id) { return (state.commissionUpgrades || []).includes(id); }
function commissionMultiplier() { return commissionMult(state.commissionUpgrades || []); }
function activeChallengeBlocks() { return (state.activeChallenge ? challengeFor(state.activeChallenge)?.blocks : null) || {}; }
// Codex: each Archive entry logged carries a tiny permanent bonus (Cookie
// Clicker sugar-lump/codex flavour) — story and numbers together.
function codexMultiplier() { return 1 + (0.01 * (state.archive || []).length); }
function commonsRate() {
  let rate = 0.1;
  if (hasCommission('commons-fast')) rate *= 2;
  if ((state.challengesDone || []).includes('blackout')) rate *= 1.5;
  return rate;
}
function offlineFactor() { return hasCommission('offline-plus') ? 0.75 : OFFLINE_FACTOR; }

function stableMultiplier() {
  const nsman = state.isNsman ? 0.85 : 1;
  const supply = activeChallengeBlocks().commons ? 1 : serviceMultipliers().supply;
  return (1 + (0.01 * state.commendations)) * nsman * supply
    * serviceRecordMultiplier((state.achievements || []).length)
    * challengeRewardMult(state.challengesDone || [])
    * codexMultiplier()
    * tempoMultiplier() * commissionMultiplier() * seasonMultiplier();
}

function temporaryMultiplierAt(realAt) {
  const virtualAt = clock.now(realAt);
  const ipptStartedAt = state.ipptExpiresAt ? state.ipptExpiresAt - IPPT_INTERVAL_MS : 0;
  const ippt = ipptStartedAt && virtualAt >= ipptStartedAt && virtualAt < state.ipptExpiresAt ? (1 + state.ipptBonus) : 1;
  const ict = state.ictStartedAt && virtualAt >= state.ictStartedAt && virtualAt < state.ictEndsAt ? 2 : 1;
  const surge = state.surgeStartedAt && realAt >= state.surgeStartedAt && realAt < state.surgeUntil ? SURGE_MULT : 1;
  const signal = state.signalUntil && realAt >= state.signalUntil - SIGNAL_FRENZY_MS && realAt < state.signalUntil ? (state.signalMult || 1) : 1;
  return ippt * ict * surge * signal;
}

// A legible account of why output is the number it is. Each entry is a
// multiplicative factor on the raw unit output; the UI lists them so players
// can see which lever moves the total.
function multiplierBreakdown() {
  const realAt = Date.now();
  const virtualAt = clock.now(realAt);
  const supply = serviceMultipliers().supply;
  const factors = [];
  if (state.commendations > 0) factors.push({ label: 'Commendations', factor: 1 + (0.01 * state.commendations), note: `${state.commendations} logged` });
  if (state.isNsman) factors.push({ label: 'NSman file', factor: 0.85, note: 'reservist output' });
  if (supply !== 1) factors.push({ label: 'Supply commons', factor: supply, note: 'server sustain' });
  const ipptStartedAt = state.ipptExpiresAt ? state.ipptExpiresAt - IPPT_INTERVAL_MS : 0;
  if (ipptStartedAt && virtualAt >= ipptStartedAt && virtualAt < state.ipptExpiresAt) factors.push({ label: `IPPT ${state.ipptGrade ? state.ipptGrade.toUpperCase() : ''}`.trim(), factor: 1 + state.ipptBonus, note: 'temporary' });
  if (state.ictStartedAt && virtualAt >= state.ictStartedAt && virtualAt < state.ictEndsAt) factors.push({ label: 'ICT call-up', factor: 2, note: 'temporary' });
  if (state.surgeStartedAt && realAt >= state.surgeStartedAt && realAt < state.surgeUntil) factors.push({ label: 'Breath surge', factor: SURGE_MULT, note: 'burst window' });
  return { base: baseRate(), factors, total: rate() };
}

function baseRate() {
  const upgrades = state.upgrades || [];
  const fusionCount = state.vocation === 'dis' ? Number(state.units[7] || 0) : 0;
  const blocks = activeChallengeBlocks();
  const base = activeUnits().reduce((total, unit) => {
    if (blocks.tierAbove && unit.tier > blocks.tierAbove) return total;
    const count = Number(state.units[unit.tier] || 0);
    let tierMultiplier = 1;
    if (upgrades.includes(`tier-${unit.tier}-one`)) tierMultiplier *= 2;
    if (upgrades.includes(`tier-${unit.tier}-two`)) tierMultiplier *= 2;
    if (upgrades.includes('cross-tier') && unit.tier < 5) tierMultiplier *= 1.5;
    if (upgrades.includes('fusion-doctrine') && unit.tier !== 7) tierMultiplier *= 1 + (fusionCount * 0.01);
    tierMultiplier *= proficiencyMultiplier(count);
    return add(total, fromNumber(count * unit.output * tierMultiplier));
  }, fromNumber(0));
  return base;
}

function rate() {
  return multiply(baseRate(), personalMultiplier());
}

function earnedForElapsed(elapsedSeconds, factor, now) {
  const start = Math.min(state.lastTick, now);
  const boundaries = [start, now];
  const addVirtualBoundary = (virtualAt) => {
    if (!virtualAt) return;
    const realAt = clock.realAt(virtualAt);
    if (realAt > start && realAt < now) boundaries.push(realAt);
  };
  addVirtualBoundary(state.ipptExpiresAt - IPPT_INTERVAL_MS);
  addVirtualBoundary(state.ipptExpiresAt);
  addVirtualBoundary(state.ictStartedAt);
  addVirtualBoundary(state.ictEndsAt);
  if (state.surgeStartedAt) {
    if (state.surgeStartedAt > start && state.surgeStartedAt < now) boundaries.push(state.surgeStartedAt);
    if (state.surgeUntil > start && state.surgeUntil < now) boundaries.push(state.surgeUntil);
  }
  if (state.signalUntil) {
    const signalStart = state.signalUntil - SIGNAL_FRENZY_MS;
    if (signalStart > start && signalStart < now) boundaries.push(signalStart);
    if (state.signalUntil > start && state.signalUntil < now) boundaries.push(state.signalUntil);
  }
  boundaries.sort((left, right) => left - right);
  const base = multiply(baseRate(), stableMultiplier());
  return boundaries.slice(0, -1).reduce((total, segmentStart, index) => {
    const segmentEnd = boundaries[index + 1];
    const segmentSeconds = Math.max(0, (segmentEnd - segmentStart) / 1000);
    const segmentMultiplier = temporaryMultiplierAt(segmentStart + ((segmentEnd - segmentStart) / 2));
    return add(total, multiply(base, segmentSeconds * segmentMultiplier * factor));
  }, fromNumber(0));
}

function operationFor(event = state.currentEvent) {
  if (event?.ictOnly) return ICT_OPERATION;
  return event ? OPERATIONS[event.operationIndex % OPERATIONS.length] : OPERATIONS[state.eventIndex % OPERATIONS.length];
}

function currentOperation() { return operationFor(); }

function nextAvailableOperationIndex(startIndex = state.eventIndex) {
  const available = new Set(unlockedPillars(state.rankIndex).map((pillar) => pillar.id));
  for (let offset = 0; offset < OPERATIONS.length; offset += 1) {
    const index = (startIndex + offset) % OPERATIONS.length;
    if (available.has(OPERATIONS[index].pillar || 'military')) return index;
  }
  return 0;
}

function addFeed(text) {
  feed.unshift({ time: 'now', text });
  feed = feed.slice(0, 7);
}

function resolveEvent(event, resolvedAt) {
  const operation = operationFor(event);
  const committed = Number(state.posture[operation.sector] || 0);
  const sector = SECTORS.find((candidate) => candidate.id === operation.sector);
  const serviceMultiplier = sector?.primary?.toLowerCase() === state.vocation ? 2 : sector?.secondary?.toLowerCase() === state.vocation ? 1.5 : 1;
  const intelEfficiency = 1 + (0.2 * commonsLog(state.commons.intel));
  const stats = defenceStats();
  const pillarFactor = operation.pillar === 'civil' ? 1 + (stats.resilience / 200)
    : operation.pillar === 'economic' ? 1 + (stats.stockpiles / 200)
      : operation.pillar === 'social' ? 1 + (stats.cohesion / 200)
        : operation.pillar === 'digital' ? 1 + (stats.hardening / 200)
          : operation.pillar === 'psychological' ? 1 + (stats.resolve / 200) : 1;
  const regularForceBaseline = operation.strength * 0.65;
  const playerForce = Math.max(1, toNumber(rate())) * (committed / 100) * serviceMultiplier * intelEfficiency * pillarFactor;
  const totalForce = regularForceBaseline + playerForce;
  const outcome = totalForce >= operation.strength * 1.5 ? 'contained' : totalForce >= operation.strength ? 'held' : totalForce >= operation.strength * 0.5 ? 'partial' : 'baseline';
  const outcomeFactor = { contained: 1.35, held: 1, partial: 0.8, baseline: 0.65 }[outcome] * (event.responded ? 1.1 : 1);
  const reward = multiply(rate(), EVENT_REWARD_SECONDS * outcomeFactor);
  state.credits = add(state.credits, reward);
  state.lifetime = add(state.lifetime, reward);
  const outcomeText = event.responded ? `${outcome}; command improved the result` : `${outcome}; regular-force baseline active`;
  state.glimmer += event.responded ? 2 : 1;
  state.archive = Array.from(new Set(state.archive.concat(operation.name)));
  state.statistics.eventsSeen += 1;
  if (event.responded) state.statistics.eventsResponded += 1; else state.statistics.eventsMissed += 1;
  addFeed(`${operation.name}: ${outcomeText}. +${format(reward)} Credits.`);
  state.eventHistory = [{ operationIndex: event.operationIndex, resolvedAt, responded: Boolean(event.responded), outcome: outcomeText }].concat(state.eventHistory).slice(0, 12);
  state.visualEffects = { kind: operation.visual, outcome, name: operation.name, radio: operation.radio, startedAt: Date.now(), until: Date.now() + 7000 };
  if (!event.ictOnly) {
    state.eventIndex = (event.operationIndex + 1) % OPERATIONS.length;
    state.eventLastAt = resolvedAt;
    state.nextEventAt = resolvedAt + nextInterval(state.worldSeed, state.eventIndex + state.eventHistory.length);
  }
  state.currentEvent = null;
}

function syncEvents(virtualNow = clock.now()) {
  if (cloudMode || state.currentEvent?.server) return;
  let guard = 0;
  while (guard < 32) {
    guard += 1;
    if (state.currentEvent) {
      if (virtualNow < state.currentEvent.resolvesAt) return;
      resolveEvent(state.currentEvent, state.currentEvent.resolvesAt);
      continue;
    }
    if (virtualNow < state.nextEventAt) return;
    const operationIndex = nextAvailableOperationIndex();
    const operation = OPERATIONS[operationIndex];
    const duration = Math.max(30_000, EVENT_WINDOW_MS * serviceMultipliers().readiness);
    state.currentEvent = {
      operationIndex,
      startedAt: state.nextEventAt,
      resolvesAt: state.nextEventAt + duration,
      responded: false,
      intelRevealedAt: state.nextEventAt - (serviceMultipliers().intelLeadHours * 60 * 60 * 1000),
    };
    addFeed(`Situation developing: ${operation.name}. The regulars are already moving.`);
    if (virtualNow >= state.currentEvent.resolvesAt) continue;
    return;
  }
}

function syncLifecycle(virtualNow = clock.now()) {
  if (cloudMode) return;
  if (state.ictStartedAt && virtualNow >= state.ictEndsAt) {
    if (!state.ictClaimed) {
      state.commendations += 1;
      state.glimmer += 5;
      state.statistics.ictCompleted += 1;
      addFeed('ICT complete. One Commendation logged; book out properly when convenient.');
    }
    state.ictClaimed = true;
    state.ictStartedAt = 0;
    state.ictEndsAt = 0;
    state.ictNextAt = virtualNow + ICT_INTERVAL_MS;
  }
  if (state.ipptExpiresAt && virtualNow >= state.ipptExpiresAt) {
    state.ipptBonus = 0;
    state.ipptExpiresAt = 0;
    addFeed('IPPT bonus expired. Nothing lost; the next check-in is optional.');
  }
}

function ordGain() {
  if (state.lifetime.e < 12) return 0;
  const scaled = state.lifetime.e - 12 > 15 ? Infinity : state.lifetime.m * (10 ** (state.lifetime.e - 12));
  return Math.max(0, Math.floor(10 * Math.sqrt(scaled)) - state.commendations);
}

function openOrd() {
  advance();
  if (state.rankIndex < 10 || ordGain() <= 0) return render();
  state.pendingOverlay = 'ord';
  saveState();
  render();
}

function confirmOrd() {
  if (cloudMode) {
    syncCloudIntent(cloud.ord());
    return;
  }
  advance();
  const gain = ordGain();
  if (state.rankIndex < 10 || gain <= 0) return render();
  const now = Date.now();
  const virtualNow = clock.now();
  const oldRecord = Math.max(state.rankRecord, state.rankIndex);
  state = {
    ...state,
    credits: fromNumber(hasCommission('seed-credits') ? 10000 : 50),
    lifetime: fromNumber(0),
    units: { 1: 1 },
    posture: {},
    marketLots: 0,
    // Operational Tempo and managers are active-service purchases (bought with
    // credits, not a permanent meta-currency), so they wipe on ORD like units
    // and upgrades — matching resetService() and the authoritative saf_ord().
    tempoLevel: 0,
    managers: [],
    rankIndex: 0,
    rankRecord: oldRecord,
    enlistmentNumber: state.enlistmentNumber + 1,
    isNsman: true,
    commendations: state.commendations + gain,
    upgrades: [],
    pillars: ['military'],
    ictNextAt: virtualNow + ICT_INTERVAL_MS,
    ictStartedAt: 0,
    ictEndsAt: 0,
    ictClaimed: false,
    ipptNextAt: virtualNow + IPPT_INTERVAL_MS,
    ipptBonus: 0,
    ipptGrade: null,
    ipptExpiresAt: 0,
    ipptSteps: 0,
    eventIndex: 0,
    eventLastAt: virtualNow,
    nextEventAt: virtualNow + nextInterval(state.worldSeed, state.enlistmentNumber),
    currentEvent: null,
    commandIndex: 0,
    pendingOverlay: null,
    statistics: { ...state.statistics, ords: state.statistics.ords + 1 },
    lastTick: now,
  };
  addFeed(`ORD complete. ${gain} Commendation${gain === 1 ? '' : 's'} carried into enlistment ${state.enlistmentNumber}.`);
  saveState();
  render();
}

function chooseVocation(vocationId) {
  if (!VOCATIONS.some((vocation) => vocation.id === vocationId) || vocationId === state.vocation) return;
  if (cloudMode) {
    syncCloudIntent(cloud.enlist(vocationId));
    return;
  }
  if (state.rankIndex > 1 || state.lifetime.e > 3) {
    addFeed('Vocation appeal noted. A new stream is available after ORD.');
    render();
    return;
  }
  state.vocation = vocationId;
  state.units = { 1: 1 };
  addFeed(`Vocation assignment confirmed: ${vocationFor(vocationId).name}.`);
  saveState();
  render();
}

function advanceTutorial() {
  if (state.tutorialComplete) return;
  state.tutorialStep = Math.min(2, state.tutorialStep + 1);
  saveState();
  render();
}

function completeTutorial() {
  if (state.tutorialComplete || state.tutorialStep < 2) return;
  state.tutorialComplete = true;
  state.tutorialStep = 3;
  state.credits = fromNumber(50);
  state.lifetime = fromNumber(1000);
  state.rankIndex = 1;
  state.rankRecord = Math.max(state.rankRecord, 1);
  state.posture = { 6: 100 };
  addFeed(`Vocation assignment confirmed: ${vocationFor(state.vocation).name}. The regulars are already on duty.`);
  saveState();
  render();
}

function startIct() {
  advance();
  const now = clock.now();
  if (!state.isNsman || state.ictStartedAt || now < state.ictNextAt) return render();
  state.ictOptIn = true;
  state.ictStartedAt = now;
  state.ictEndsAt = now + ICT_DURATION_MS;
  state.ictClaimed = false;
  if (!state.currentEvent) {
    state.currentEvent = { operationIndex: -1, ictOnly: true, startedAt: now, resolvesAt: now + EVENT_WINDOW_MS, responded: false, intelRevealedAt: now };
  }
  addFeed('ICT booked in. Output is doubled for the next 48 hours; missing it costs nothing.');
  saveState();
  render();
}

function openIppt() {
  advance();
  if (clock.now() < state.ipptNextAt) return render();
  state.pendingOverlay = 'ippt';
  state.ipptSteps = 0;
  saveState();
  render();
}

function completeIpptStep() {
  if (state.pendingOverlay !== 'ippt') return;
  state.ipptSteps = Math.min(3, state.ipptSteps + 1);
  saveState();
  render();
}

function finishIppt(grade) {
  const bonuses = { pass: 0.05, incentive: 0.1, silver: 0.15, gold: 0.25 };
  if (state.pendingOverlay !== 'ippt' || state.ipptSteps < 3 || !bonuses[grade]) return render();
  if (cloudMode) {
    state.pendingOverlay = null;
    syncCloudIntent(cloud.setIppt(grade));
    return;
  }
  const now = clock.now();
  state.ipptGrade = grade;
  state.ipptBonus = bonuses[grade];
  state.ipptExpiresAt = now + IPPT_INTERVAL_MS;
  state.ipptNextAt = now + IPPT_INTERVAL_MS;
  state.pendingOverlay = null;
  state.statistics.ipptChecks += 1;
  addFeed(`IPPT ${grade.toUpperCase()} logged. +${Math.round(bonuses[grade] * 100)}% output for seven days.`);
  saveState();
  render();
}

function buyUpgrade(upgradeId) {
  if (cloudMode) {
    syncCloudIntent(cloud.buyUpgrade(upgradeId));
    return;
  }
  advance();
  if (activeChallengeBlocks().upgrades) { addFeed('Austerity Posting: no upgrades this deployment.'); return render(); }
  const upgrade = upgradeFor(upgradeId);
  if (!upgrade || state.upgrades.includes(upgrade.id) || state.rankIndex < upgrade.unlock || (upgrade.requires && !state.upgrades.includes(upgrade.requires))) return render();
  const cost = fromNumber(upgrade.cost);
  if (compare(state.credits, cost) < 0) {
    addFeed(`Not enough Credits for ${upgrade.name}.`);
    return render();
  }
  state.credits = subtract(state.credits, cost);
  state.upgrades = state.upgrades.concat(upgrade.id);
  state.statistics.upgradesBought += 1;
  addFeed(`${upgrade.name} adopted. Output doctrine updated.`);
  saveState();
  render();
}

function currentCommand() {
  return commandFor(state.commandIndex);
}

// Advance past any field order whose escalating target has outrun its ceiling
// (see orderReachable). Bounded by a guard because 'formation' is always
// reachable, so the loop always terminates on a claimable order.
function skipUnreachableCommands() {
  if (cloudMode) return;
  let guard = 0;
  while (guard < 8 && !orderReachable(currentCommand(), UPGRADES.length, RANKS.length - 1)) {
    state.commandIndex += 1;
    guard += 1;
  }
}

function commandReady(order = currentCommand()) {
  return order.progressFor(state) >= order.targetFor(order.round);
}

function claimCommand() {
  advance();
  const order = currentCommand();
  if (!commandReady(order)) return render();
  if (cloudMode) {
    syncCloudIntent(cloud.claimCommand(order.index), `${order.label}: order complete. Shared reward logged.`);
    return;
  }
  const reward = fromNumber(order.rewardFor(order.round));
  state.credits = add(state.credits, reward);
  state.lifetime = add(state.lifetime, reward);
  state.glimmer += order.glimmerFor(order.round);
  const commonsKey = vocationFor(state.vocation).commons;
  state.commons[commonsKey] = add(state.commons[commonsKey], multiply(reward, 0.1));
  state.commandIndex += 1;
  state.statistics.ordersCompleted = Number(state.statistics.ordersCompleted || 0) + 1;
  state.visualEffects = { kind: 'containment', outcome: 'contained', name: order.label, radio: 'FIELD ORDER / REWARD LOGGED', startedAt: Date.now(), until: Date.now() + 7000 };
  addFeed(`${order.label}: order complete. +${format(reward)} Credits and +${order.glimmerFor(order.round)} Glimmer.`);
  const previousRank = state.rankIndex;
  state.rankIndex = currentRankIndex();
  if (state.rankIndex > previousRank) {
    state.rankRecord = Math.max(state.rankRecord, state.rankIndex);
    state.pillars = unlockedPillars(state.rankIndex).map((pillar) => pillar.id);
    addFeed(`Promotion received: ${RANKS[state.rankIndex].name}.`);
    notePillarUnlock(previousRank);
  }
  saveState();
  render();
}

function advance(factor = (inBackground ? offlineFactor() : 1)) {
  const now = Date.now();
  const elapsed = Math.max(0, (now - state.lastTick) / 1000);
  let earned = fromNumber(0);
  if (elapsed > 0) {
    earned = earnedForElapsed(elapsed, factor, now);
    state.credits = add(state.credits, earned);
    state.lifetime = add(state.lifetime, earned);
    const commonsKey = vocationFor(state.vocation).commons;
    state.commons[commonsKey] = add(state.commons[commonsKey], multiply(earned, commonsRate()));
    // Time spent away (offline / backgrounded) banks Breath, capped so it stays
    // a return bonus rather than a reason to farm absence.
    if (factor < 1) state.breath = Math.min(BREATH_CAP_S, state.breath + elapsed);
    state.lastTick = now;
    const previousRank = state.rankIndex;
    state.rankIndex = currentRankIndex();
    if (state.rankIndex > previousRank) {
      state.rankRecord = Math.max(state.rankRecord, state.rankIndex);
      state.pillars = unlockedPillars(state.rankIndex).map((pillar) => pillar.id);
      addFeed(`Promotion received: ${RANKS[state.rankIndex].name}. ${PILLARS.find((pillar) => pillar.unlock === state.rankIndex) ? 'A Total Defence pillar is now online.' : ''}`);
      notePillarUnlock(previousRank);
    }
  }
  checkChallenge();
  skipUnreachableCommands();
  syncAchievements();
  syncSignal();
  syncLifecycle(clock.now());
  syncEvents(clock.now());
  return earned;
}

function unitCost(unit) {
  return unitCostForCount(unit, 1);
}

function unitGrowthFor() {
  const steeper = activeChallengeBlocks().costGrowth;
  return steeper ? 1.15 * steeper : 1.15;
}

function unitDiscountFor(unit) {
  // Skeleton Crew's completion reward: tiers 1-3 stay 20% cheaper.
  return (state.challengesDone || []).includes('skeleton') && unit.tier <= 3 ? 0.8 : 1;
}

function unitCostForCount(unit, amount) {
  const count = Number(state.units[unit.tier] || 0);
  return multiply(geometricCostSum(unit.cost, count, amount, unitGrowthFor()), unitDiscountFor(unit));
}

function maxAffordableCount(unit) {
  const count = Number(state.units[unit.tier] || 0);
  const budget = divide(state.credits, unitDiscountFor(unit));
  return maxAffordable(unit.cost, count, budget, unitGrowthFor());
}

function totalPosture(exclude = null) {
  return Object.entries(state.posture).reduce((sum, [id, pct]) => sum + (String(id) === String(exclude) ? 0 : Number(pct || 0)), 0);
}

function setPosture(sectorId, amount) {
  if (cloudMode) {
    const next = Math.max(0, Math.min(100, amount));
    if (totalPosture(sectorId) + next > 100) {
      addFeed('Posture allocation capped at 100%.');
      return render();
    }
    syncCloudIntent(cloud.setPosture({ ...state.posture, [sectorId]: next }));
    return;
  }
  advance();
  const next = Math.max(0, Math.min(100, amount));
  if (totalPosture(sectorId) + next > 100) {
    addFeed('Posture allocation capped at 100%.');
    render();
    return;
  }
  state.posture[sectorId] = next;
  const sector = SECTORS.find((candidate) => candidate.id === sectorId);
  addFeed(`Posture updated: ${sector.name} at ${next}%.`);
  saveState();
  render();
}

function selectSector(sectorId) {
  selectedSectorId = sectorId;
  render();
}

function respondToEvent() {
  if (cloudMode && state.currentEvent?.server) {
    const event = state.currentEvent;
    const operation = operationFor(event);
    event.responded = true;
    selectedSectorId = operation.sector;
    const current = Number(state.posture[operation.sector] || 0);
    const next = Math.min(100, current + 10);
    if (totalPosture(operation.sector) + (next - current) <= 100) {
      syncCloudIntent(cloud.setPosture({ ...state.posture, [operation.sector]: next }));
    }
    addFeed(`Command accepted: ${operation.command}. The server will resolve the shared watch.`);
    render();
    return;
  }
  advance();
  const event = state.currentEvent;
  if (!event) return render();
  const operation = operationFor(event);
  if (clock.now() >= event.resolvesAt) return render();
  event.responded = true;
  selectedSectorId = operation.sector;
  const current = Number(state.posture[operation.sector] || 0);
  if (totalPosture(operation.sector) + Math.min(10, 100 - current) <= 100) state.posture[operation.sector] = Math.min(100, current + 10);
  state.visualEffects = { kind: operation.visual, name: operation.name, radio: operation.radio, startedAt: Date.now(), until: Date.now() + 12000 };
  addFeed(`Command accepted: ${operation.command}. Response units are visible in ${SECTORS.find((sector) => sector.id === operation.sector).name}.`);
  saveState();
  render();
}

function surgeDurationFor(breathSeconds) {
  return Math.round(Math.max(MIN_SURGE_MS, Math.min(MAX_SURGE_MS, breathSeconds * SURGE_MS_PER_BREATH_S)));
}

// DIS's distinct verb: act on intel before a threat develops. A preempted
// watch is closed early for a clean reward and never plays out as a threat —
// it simply greys out with a lead-time stamp (DESIGN-TOTAL-DEFENCE §4.1).
function canPreempt(event = state.currentEvent) {
  return state.vocation === 'dis' && event && !event.responded && !event.preempted;
}

function preemptEvent() {
  const event = state.currentEvent;
  if (!canPreempt(event)) return render();
  if (cloudMode) {
    if (!event.server || !event.serverId) return render();
    syncCloudIntent(cloud.preemptThreat(event.serverId), 'Preemption committed. Intel closed the shared watch early.');
    return;
  }
  advance();
  if (event.server) return render();
  const virtualNow = clock.now();
  if (virtualNow >= event.resolvesAt) return render();
  const operation = operationFor(event);
  const leadHours = Math.max(0, (event.resolvesAt - virtualNow) / 3600000);
  const reward = multiply(rate(), EVENT_REWARD_SECONDS * 1.25);
  state.credits = add(state.credits, reward);
  state.lifetime = add(state.lifetime, reward);
  state.glimmer += 2;
  state.archive = Array.from(new Set(state.archive.concat(operation.name)));
  state.statistics.eventsSeen += 1;
  state.statistics.eventsResponded += 1;
  state.eventHistory = [{ operationIndex: event.operationIndex, resolvedAt: virtualNow, responded: true, outcome: 'preempted; intel closed it early' }].concat(state.eventHistory).slice(0, 12);
  state.visualEffects = { kind: 'preempt', outcome: 'preempted', name: operation.name, radio: `PREEMPTED / ${leadHours.toFixed(1)}H LEAD-TIME STAMP`, startedAt: Date.now(), until: Date.now() + 7000 };
  if (!event.ictOnly) {
    state.eventIndex = (event.operationIndex + 1) % OPERATIONS.length;
    state.eventLastAt = virtualNow;
    state.nextEventAt = virtualNow + nextInterval(state.worldSeed, state.eventIndex + state.eventHistory.length);
  }
  state.currentEvent = null;
  addFeed(`${operation.name}: preempted before it developed. +${format(reward)} Credits · ${leadHours.toFixed(1)}h lead.`);
  saveState();
  render();
}

function activateSurge() {
  advance();
  const now = Date.now();
  const surgeActive = state.surgeStartedAt && now < state.surgeUntil;
  if (surgeActive || state.breath < BREATH_MIN_S) return render();
  if (cloudMode) {
    syncCloudIntent(cloud.releaseBreath(), 'Breath released. The server is applying the burst.');
    return;
  }
  const duration = surgeDurationFor(state.breath);
  state.breath = 0;
  state.surgeStartedAt = now;
  state.surgeUntil = now + duration;
  state.visualEffects = { kind: 'surge', startedAt: now, until: state.surgeUntil };
  addFeed(`Breath released. Output ×${SURGE_MULT} for ${Math.round(duration / 1000)}s.`);
  saveState();
  render();
}

function automationUnlocked(kind) {
  if (hasCommission('auto-early')) return true;
  const record = Math.max(state.rankIndex, state.rankRecord);
  return kind === 'book' ? record >= AUTO_BOOK_RANK : record >= AUTO_RESPOND_RANK;
}

// Runs once per tick. Kept local-only so it never spams the authoritative
// server with per-second intent; cloud players buy and respond by hand.
function runAutomation() {
  if (cloudMode) return;
  // Per-tier managers each book their own tier once per tick.
  (state.managers || []).forEach((tier) => {
    const unit = activeUnits().find((candidate) => candidate.tier === tier);
    if (unit && state.rankIndex >= unit.unlock && compare(state.credits, unitCostForCount(unit, 1)) >= 0) {
      state.credits = subtract(state.credits, unitCostForCount(unit, 1));
      state.units[tier] = Number(state.units[tier] || 0) + 1;
      state.statistics.unitsBooked += 1;
    }
  });
  if (state.autoBook && automationUnlocked('book')) {
    const affordable = activeUnits().filter((unit) => state.rankIndex >= unit.unlock && compare(state.credits, unitCostForCount(unit, 1)) >= 0);
    if (affordable.length) {
      const unit = affordable[affordable.length - 1];
      state.credits = subtract(state.credits, unitCostForCount(unit, 1));
      state.units[unit.tier] = Number(state.units[unit.tier] || 0) + 1;
      state.statistics.unitsBooked += 1;
    }
  }
  if (state.autoRespond && automationUnlocked('respond')) {
    const event = state.currentEvent;
    if (event && !event.responded && !event.server && clock.now() < event.resolvesAt) {
      const operation = operationFor(event);
      event.responded = true;
      const current = Number(state.posture[operation.sector] || 0);
      if (totalPosture(operation.sector) + Math.min(10, 100 - current) <= 100) state.posture[operation.sector] = Math.min(100, current + 10);
    }
  }
}

function buyUnit(tier, amount = 1) {
  const unit = activeUnits().find((candidate) => candidate.tier === tier);
  if (!unit || state.rankIndex < unit.unlock) return;
  if (activeChallengeBlocks().tierAbove && unit.tier > activeChallengeBlocks().tierAbove) {
    addFeed('Skeleton Crew: that tier is out of establishment this deployment.');
    return render();
  }
  const purchaseCount = amount === 'max'
    ? Math.min(1000, maxAffordableCount(unit))
    : Math.max(1, Math.min(100, Number(amount) || 1));
  if (purchaseCount < 1) {
    addFeed(`Not enough Credits for another ${unit.name}.`);
    return render();
  }
  if (cloudMode) {
    syncCloudIntent(cloud.buyUnit(tier, purchaseCount));
    return;
  }
  advance();
  const cost = unitCostForCount(unit, purchaseCount);
  if (compare(state.credits, cost) < 0) {
    addFeed(`Not enough Credits for another ${unit.name}.`);
    render();
    return;
  }
  state.credits = subtract(state.credits, cost);
  state.units[tier] = Number(state.units[tier] || 0) + purchaseCount;
  state.statistics.unitsBooked += purchaseCount;
  addFeed(`${purchaseCount} ${unit.name}${purchaseCount === 1 ? '' : 's'} booked in. Output is now ${format(rate())}/s.`);
  saveState();
  render();
}

// ---- Rare Signal (golden-cookie clickable) --------------------------------
function scheduleSignal(realNow = Date.now()) {
  state.nextSignalAt = realNow + SIGNAL_MIN_INTERVAL + Math.random() * (SIGNAL_MAX_INTERVAL - SIGNAL_MIN_INTERVAL);
}

function syncSignal(realNow = Date.now()) {
  if (cloudMode) return;
  if (state.signalUntil && realNow >= state.signalUntil) { state.signalUntil = 0; state.signalKind = null; state.signalMult = 1; }
  if (!state.nextSignalAt) scheduleSignal(realNow);
  if (state.signal && realNow >= state.signal.expiresAt) { state.signal = null; scheduleSignal(realNow); }
  if (!state.signal && !inBackground && realNow >= state.nextSignalAt) {
    const kinds = ['frenzy', 'windfall', 'breath', 'intel'];
    state.signal = { kind: kinds[Math.floor(Math.random() * kinds.length)], spawnedAt: realNow, expiresAt: realNow + SIGNAL_WINDOW };
  }
}

function catchSignal() {
  if (!state.signal || cloudMode) return render();
  const kind = state.signal.kind;
  const now = Date.now();
  state.signal = null;
  scheduleSignal(now);
  state.statistics.signalsCaught = Number(state.statistics.signalsCaught || 0) + 1;
  if (kind === 'frenzy') {
    state.signalUntil = now + SIGNAL_FRENZY_MS; state.signalKind = 'frenzy'; state.signalMult = 7;
    addFeed('Rare signal caught: FRENZY. ×7 output for 30s.');
  } else if (kind === 'windfall') {
    const gain = multiply(rate(), 120);
    state.credits = add(state.credits, gain); state.lifetime = add(state.lifetime, gain);
    addFeed(`Rare signal caught: WINDFALL. +${format(gain)} Credits.`);
  } else if (kind === 'breath') {
    state.breath = BREATH_CAP_S;
    addFeed('Rare signal caught: SECOND WIND. Breath refilled to cap.');
  } else {
    state.signalUntil = now + SIGNAL_FRENZY_MS; state.signalKind = 'intel'; state.signalMult = 2;
    addFeed('Rare signal caught: LUCKY INTEL. ×2 output for 30s.');
  }
  state.visualEffects = { kind: 'surge', startedAt: now, until: now + 4000 };
  saveState();
  render();
}

// ---- Service Record (achievements) ----------------------------------------
function syncAchievements() {
  const newly = evaluateAchievements(state, state.achievements);
  if (!newly.length) return;
  if (cloudMode) {
    if (achievementSyncPending) return;
    achievementSyncPending = true;
    cloud.syncAchievements((state.achievements || []).concat(newly)).then(({ data, error }) => {
      achievementSyncPending = false;
      if (!error && mergeCloudPayload(data)) { saveState(); render(); }
    });
    return;
  }
  state.achievements = (state.achievements || []).concat(newly);
  newly.forEach((id) => {
    const badge = ACHIEVEMENTS.find((entry) => entry.id === id);
    state.glimmer += 1;
    if (badge) addFeed(`Service Record: ${badge.name} earned. +2% output, +1 Glimmer.`);
  });
}

// ---- Shared reset used by Deployments and Commission ----------------------
function resetService(extra = {}) {
  const now = Date.now();
  const virtualNow = clock.now();
  const seed = hasCommission('seed-credits') ? 10000 : 50;
  Object.assign(state, {
    credits: fromNumber(seed),
    lifetime: fromNumber(0),
    units: { 1: 1 },
    posture: {},
    rankIndex: 0,
    upgrades: [],
    pillars: ['military'],
    ictStartedAt: 0, ictEndsAt: 0, ictClaimed: false,
    ipptBonus: 0, ipptGrade: null, ipptExpiresAt: 0, ipptSteps: 0,
    ipptNextAt: virtualNow + IPPT_INTERVAL_MS,
    ictNextAt: virtualNow + ICT_INTERVAL_MS,
    eventIndex: 0, eventLastAt: virtualNow,
    nextEventAt: virtualNow + nextInterval(state.worldSeed, (state.enlistmentNumber || 1) + 1),
    currentEvent: null,
    commandIndex: 0,
    tempoLevel: 0,
    managers: [],
    marketLots: 0,
    pendingOverlay: null,
    lastTick: now,
  });
  Object.assign(state, extra);
}

// ---- Deployments (challenge runs) -----------------------------------------
const CHALLENGE_UNLOCK_RANK = 5;
function challengesUnlocked() { return Math.max(state.rankIndex, state.rankRecord) >= CHALLENGE_UNLOCK_RANK; }

function startChallenge(id) {
  if (cloudMode) { syncCloudIntent(cloud.startChallenge(id)); return; }
  const challenge = challengeFor(id);
  if (!challenge || state.activeChallenge || !challengesUnlocked()) return render();
  advance();
  resetService({ activeChallenge: id });
  addFeed(`Deployed: ${challenge.name}. ${challenge.detail}`);
  saveState();
  render();
}

function abandonChallenge() {
  if (!state.activeChallenge) return render();
  if (cloudMode) { syncCloudIntent(cloud.abandonChallenge()); return; }
  advance();
  resetService({ activeChallenge: null });
  addFeed('Deployment abandoned. Back to standing watch.');
  saveState();
  render();
}

function checkChallenge() {
  if (!state.activeChallenge) return;
  const challenge = challengeFor(state.activeChallenge);
  if (!challenge || state.rankIndex < challenge.target) return;
  if (!(state.challengesDone || []).includes(challenge.id)) state.challengesDone = (state.challengesDone || []).concat(challenge.id);
  addFeed(`Deployment complete: ${challenge.name}. Reward secured — ${challenge.reward}`);
  resetService({ activeChallenge: null });
}

// ---- Regular Commission (second prestige) ---------------------------------
function canCommission() { return state.commendations >= COMMISSION_UNLOCK_COMMENDATIONS && commandPointGain(state.commendations) > 0; }

function openCommission() {
  advance();
  if (!canCommission()) return render();
  state.pendingOverlay = 'commission';
  saveState();
  render();
}

function confirmCommission() {
  if (cloudMode) { state.pendingOverlay = null; syncCloudIntent(cloud.commission()); return; }
  advance();
  if (!canCommission()) return render();
  const gain = commandPointGain(state.commendations);
  state.commandPoints = (state.commandPoints || 0) + gain;
  state.commissions = (state.commissions || 0) + 1;
  state.commendations = 0;
  state.isNsman = false;
  state.enlistmentNumber = 1;
  resetService({ activeChallenge: null, pendingOverlay: null });
  addFeed(`Commissioned as a Regular. +${gain} Command Points (total ${state.commandPoints}).`);
  saveState();
  render();
}

function buyCommissionUpgrade(id) {
  if (cloudMode) { syncCloudIntent(cloud.buyCommissionUpgrade(id)); return; }
  advance();
  const upgrade = commissionFor(id);
  if (!upgrade || hasCommission(id) || (upgrade.requires && !hasCommission(upgrade.requires))) return render();
  if ((state.commandPoints || 0) < upgrade.cost) { addFeed('Not enough Command Points.'); return render(); }
  state.commandPoints -= upgrade.cost;
  state.commissionUpgrades = (state.commissionUpgrades || []).concat(id);
  addFeed(`Commission: ${upgrade.name} adopted.`);
  saveState();
  render();
}

// ---- Operational Tempo & managers -----------------------------------------
function buyTempo() {
  if (cloudMode) { syncCloudIntent(cloud.buyTempo()); return; }
  advance();
  const cost = tempoCost();
  if (compare(state.credits, cost) < 0) { addFeed('Not enough Credits to raise Tempo.'); return render(); }
  state.credits = subtract(state.credits, cost);
  state.tempoLevel = (Number(state.tempoLevel) || 0) + 1;
  addFeed(`Operational Tempo raised to ${state.tempoLevel}. +10% output.`);
  saveState();
  render();
}

let lastMarketWalk = 0;

// The sparkline used to live in a module-local array, so every reload threw the
// chart away and left an empty box until enough ticks had accumulated. History
// belongs in the save: it is what the player has actually observed, and it is
// the only part of the market they get to reason about.
function marketHistory() {
  if (!Array.isArray(state.marketHistory)) state.marketHistory = [];
  // A save that has never ticked still has a known price. Seeding from it draws
  // a flat line at that price, which is the honest reading — no movement has
  // been observed yet — rather than a blank panel or invented volatility.
  if (state.marketHistory.length < 2) {
    const price = state.marketPrice || MARKET_BASE;
    state.marketHistory = [price, price];
  }
  return state.marketHistory;
}

function recordMarketPrice(price) {
  const history = marketHistory();
  history.push(Math.round(price * 100) / 100);
  while (history.length > MARKET_HISTORY_POINTS) history.shift();
}

function marketTick() {
  if (cloudMode) return;
  const now = Date.now();
  if (now - lastMarketWalk < 4000) return;
  lastMarketWalk = now;
  state.marketPrice = walkMarketPrice(state.marketPrice || MARKET_BASE);
}

function marketPositionCap() { return Math.max(toNumber(rate()) * MARKET_POSITION_SECONDS, (state.marketPrice || MARKET_BASE) * 10); }

function buyLots(lots) {
  if (cloudMode) { syncCloudIntent(cloud.marketBuy(lots)); return; }
  advance();
  const price = state.marketPrice || MARKET_BASE;
  const cost = fromNumber(lots * price * (1 + MARKET_FEE));
  if (compare(state.credits, cost) < 0) { addFeed('Not enough Credits for that position.'); return render(); }
  if ((state.marketLots + lots) * price > marketPositionCap()) { addFeed('Position cap reached — sell before buying more.'); return render(); }
  state.credits = subtract(state.credits, cost);
  state.marketLots += lots;
  addFeed(`Bought ${lots} lot${lots === 1 ? '' : 's'} at ${format(fromNumber(price))}.`);
  saveState();
  render();
}

function sellLots(lots) {
  if (cloudMode) { syncCloudIntent(cloud.marketSell(lots)); return; }
  advance();
  const sell = Math.min(lots, state.marketLots);
  if (sell <= 0) { addFeed('No position to sell.'); return render(); }
  const price = state.marketPrice || MARKET_BASE;
  state.credits = add(state.credits, fromNumber(sell * price * (1 - MARKET_FEE)));
  state.marketLots -= sell;
  addFeed(`Sold ${sell} lot${sell === 1 ? '' : 's'} at ${format(fromNumber(price))}.`);
  saveState();
  render();
}

function managerCost(unit) { return fromNumber(unit.cost * 30); }

function buyManager(tier) {
  if (cloudMode) { syncCloudIntent(cloud.buyManager(tier)); return; }
  advance();
  if ((state.managers || []).includes(tier)) return render();
  const unit = activeUnits().find((candidate) => candidate.tier === tier);
  if (!unit) return render();
  const cost = managerCost(unit);
  if (compare(state.credits, cost) < 0) { addFeed(`Not enough Credits for the ${unit.name} manager.`); return render(); }
  state.credits = subtract(state.credits, cost);
  state.managers = (state.managers || []).concat(tier);
  addFeed(`${unit.name} manager hired. That tier books itself now.`);
  saveState();
  render();
}

function rankProgress() {
  const current = RANKS[state.rankIndex];
  const next = RANKS[state.rankIndex + 1];
  if (!next) return { percent: 100, label: 'Strategic ceiling reached' };
  const lower = fromNumber(current.threshold);
  const upper = fromNumber(next.threshold);
  const span = Math.max(1, toNumber(subtract(upper, lower)));
  const complete = Math.max(0, toNumber(subtract(state.lifetime, lower)));
  return { percent: Math.min(100, Math.max(0, (complete / span) * 100)), label: `${format(subtract(upper, state.lifetime))} to ${next.short}` };
}

function timeLabel(milliseconds) { return milliseconds < 1000 ? 'now' : formatDuration(milliseconds); }

// Why a purchase is out of reach, phrased as time rather than a number: "in 2m"
// tells you to wait, where "4.95K short" leaves you to do the division. Shared
// by the unit rows and the Operational Tempo card so a disabled button always
// explains itself instead of just sitting there.
function affordLabel(cost, affordable) {
  if (affordable) return 'READY';
  const output = toNumber(rate());
  const gap = toNumber(subtract(cost, state.credits));
  return Number.isFinite(output) && output > 0 && Number.isFinite(gap) ? timeLabel((gap / output) * 1000) : 'STAND BY';
}

function situationMarkup(operation) {
  const event = state.currentEvent;
  if (!event) {
    const until = Math.max(0, state.nextEventAt - clock.now());
    return `<div class="situation-card calm-situation"><div class="situation-top"><span class="status-dot quiet-dot"></span><span class="eyebrow">CURRENT SITUATION</span><span class="situation-time">NEXT WATCH IN ${timeLabel(until)}</span></div><h2>Island routine, carefully maintained.</h2><p>Traffic is moving, the harbour is open, and every unit is quietly contributing. Optional command events arrive roughly every 30–60 minutes.</p><div class="situation-note">MISSING A WATCH NEVER WIPES PROGRESS · REGULAR-FORCE BASELINE ACTIVE</div></div>`;
  }
  const remaining = Math.max(0, event.resolvesAt - clock.now());
  return `<div class="situation-card active-situation"><div class="situation-top"><span class="status-dot"></span><span class="eyebrow">OPTIONAL COMMAND EVENT · ${operation.type}</span><span class="situation-time">${timeLabel(remaining)} WINDOW</span></div><div class="situation-copy"><span class="sector-kicker">UNATTRIBUTED SIGNAL / ZONE ${String(operation.sector).padStart(2, '0')}</span><h2>${operation.name}</h2><p>${operation.detail}</p></div><div class="situation-actions"><span>${event.responded ? 'COMMAND RECEIVED · IMPROVED RESULT SET' : 'AUTO-RESOLUTION READY · NO DESTRUCTIVE PENALTY'}</span><div class="situation-buttons">${canPreempt(event) ? '<button id="preempt-event" class="preempt-button">PREEMPT / DIS</button>' : ''}<button id="respond-event" ${event.responded ? 'disabled' : ''}>${event.responded ? 'COMMAND SENT' : operation.command.toUpperCase()}</button></div></div></div>`;
}

function unitMarkup(unit) {
  const count = Number(state.units[unit.tier] || 0);
  const unlocked = state.rankIndex >= unit.unlock;
  const profLevel = proficiencyLevel(count);
  const profNext = nextProficiency(count);
  const singleCost = unitCostForCount(unit, 1);
  const batchCost = unitCostForCount(unit, 10);
  const singleAffordable = compare(state.credits, singleCost) >= 0;
  const batchAffordable = compare(state.credits, batchCost) >= 0;
  const maxCount = unlocked ? maxAffordableCount(unit) : 0;
  return `<article class="unit-row ${unlocked ? '' : 'locked'}"><div class="unit-tier">${String(unit.tier).padStart(2, '0')}</div><div class="unit-info"><h3>${unit.name}</h3><p>${unlocked ? unit.detail : `Unlocks at ${RANKS[unit.unlock].short}`}</p></div><div class="unit-rate"><strong>${format(fromNumber(unit.output))}</strong><span>/s each</span></div><div class="unit-owned"><span>OWNED ${profLevel ? `· ★${profLevel}` : ''}</span><strong>${count}</strong><small class="prof-hint">${profNext ? `${profNext - count} to ★ (×2)` : 'MAX ★'}</small></div><div class="buy-actions"><button class="buy-button" data-buy-tier="${unit.tier}" data-buy-count="1" ${!unlocked || !singleAffordable ? 'disabled' : ''}><span>BOOK 1</span><strong>${format(singleCost)}</strong><small>${affordLabel(singleCost, singleAffordable)}</small></button><button class="buy-button batch-buy" data-buy-tier="${unit.tier}" data-buy-count="10" ${!unlocked || !batchAffordable ? 'disabled' : ''}><span>BOOK 10</span><strong>${format(batchCost)}</strong><small>${affordLabel(batchCost, batchAffordable)}</small></button><button class="buy-button max-buy" data-buy-tier="${unit.tier}" data-buy-count="max" ${!unlocked || maxCount < 1 ? 'disabled' : ''}><span>BOOK MAX</span><strong>×${maxCount}</strong><small>${maxCount >= 1 ? 'READY' : affordLabel(singleCost, singleAffordable)}</small></button>${unlocked ? ((state.managers || []).includes(unit.tier) ? '<span class="mgr-on">MANAGER ✓</span>' : `<button class="buy-button mgr-buy" data-manager="${unit.tier}" ${compare(state.credits, managerCost(unit)) < 0 ? 'disabled' : ''}><span>HIRE MANAGER</span><strong>${format(managerCost(unit))}</strong></button>`) : ''}</div></article>`;
}

function sectorNodeMarkup(sector, operation) {
  const pct = Number(state.posture[sector.id] || 0);
  const selected = selectedSectorId === sector.id;
  const activeThreat = state.currentEvent && operation.sector === sector.id;
  const [left, top] = SECTOR_COORDS[sector.id];
  return `<button class="sector-node ${selected ? 'selected-node' : ''} ${activeThreat ? 'threat-node' : ''} ${pct ? 'committed-node' : ''}" data-select-sector="${sector.id}" style="left:${left}%;top:${top}%" title="Focus ${sector.name}"><span class="node-ring"><i></i></span><span class="node-code">S-${String(sector.id).padStart(2, '0')}</span><strong>${sector.name}</strong><span class="node-posture">${pct}% POSTURE</span></button>`;
}

function vocationMarkup() {
  return `<div class="vocation-picker"><div class="subpanel-heading"><span class="eyebrow">VOCATION STREAM</span><span class="panel-hint">${state.rankIndex > 1 || state.lifetime.e > 3 ? 'APPEAL AFTER ORD' : 'ASSIGNMENT WINDOW'}</span></div><div class="vocation-options">${VOCATIONS.map((vocation) => `<button class="vocation-option ${vocation.id === state.vocation ? 'selected-vocation' : ''}" data-vocation="${vocation.id}"><span class="vocation-short">${vocation.short}</span><strong>${vocation.verb}</strong><small>${vocation.description}</small></button>`).join('')}</div></div>`;
}

function defenceStats() {
  const active = new Set(state.pillars);
  const scale = (value, factor) => Math.min(100, Math.round(commonsLog(value) * factor));
  return {
    resilience: active.has('civil') ? scale(state.commons.groundHold, 12) : 0,
    stockpiles: active.has('economic') ? Math.min(100, Math.round(Math.log10(Math.max(1, toNumber(state.credits) + 1)) * 6)) : 0,
    cohesion: active.has('social') ? Math.min(100, 50 + scale(state.commons.supply, 8)) : 0,
    hardening: active.has('digital') ? scale(state.commons.intel, 12) : 0,
    resolve: active.has('psychological') ? Math.min(100, 30 + scale(state.commons.intel, 7)) : 0,
  };
}

function pillarMarkup() {
  const stats = defenceStats();
  return `<div class="pillar-track"><div class="subpanel-heading"><span class="eyebrow">TOTAL DEFENCE PILLARS</span><span class="panel-hint">${state.pillars.length}/6 ONLINE</span></div><div class="pillar-list">${PILLARS.map((pillar) => `<div class="pillar-item ${state.pillars.includes(pillar.id) ? 'pillar-online' : 'pillar-locked'}"><span>${pillar.short}</span><div><strong>${pillar.name}</strong><small>${state.pillars.includes(pillar.id) ? pillar.description : `Unlocks at ${RANKS[pillar.unlock].short}`}</small></div></div>`).join('')}</div><div class="defence-stats"><span>RESILIENCE ${stats.resilience}%</span><span>STOCKPILES ${stats.stockpiles}%</span><span>COHESION ${stats.cohesion}%</span><span>HARDENING ${stats.hardening}%</span><span>RESOLVE ${stats.resolve}%</span></div></div>`;
}

function lifecycleMarkup() {
  const now = clock.now();
  const ictActive = state.ictStartedAt && now < state.ictEndsAt;
  const ictReady = state.isNsman && !ictActive && now >= state.ictNextAt;
  const ipptReady = now >= state.ipptNextAt;
  const ordReady = state.rankIndex >= 10 && ordGain() > 0;
  return `<div class="lifecycle-card"><div class="lifecycle-top"><span class="eyebrow">NS LIFECYCLE</span><span class="lifecycle-state">${state.isNsman ? 'NSMAN · ENLISTMENT ' + state.enlistmentNumber : phaseFor(state.rankIndex).name}</span></div><p>${state.isNsman ? 'Book in when called. The permanent record stays yours.' : phaseFor(state.rankIndex).flavour}</p><div class="lifecycle-actions">${ictActive ? `<span class="lifecycle-active">ICT ACTIVE · ${timeLabel(state.ictEndsAt - now)} REMAINING · 2× OUTPUT</span>` : state.isNsman && ictReady ? '<button data-lifecycle="ict">BOOK ICT</button>' : state.isNsman ? `<span class="lifecycle-muted">NEXT ICT IN ${timeLabel(state.ictNextAt - now)}</span>` : ''}${ipptReady ? '<button data-lifecycle="ippt">RUN IPPT</button>' : `<span class="lifecycle-muted">IPPT ${state.ipptGrade ? state.ipptGrade.toUpperCase() : 'AVAILABLE IN ' + timeLabel(state.ipptNextAt - now)}</span>`}${ordReady ? '<button data-lifecycle="ord">OPEN ORD</button>' : ''}</div></div>`;
}

function automationMarkup() {
  const bookUnlocked = automationUnlocked('book');
  const respondUnlocked = automationUnlocked('respond');
  if (!bookUnlocked && !respondUnlocked) return '';
  return `<div class="automation-track"><div class="subpanel-heading"><span class="eyebrow">STANDING ORDERS · AUTOMATION</span><span class="panel-hint">${cloudMode ? 'LOCAL WATCH ONLY' : 'HANDS-OFF'}</span></div><div class="automation-list">${bookUnlocked ? `<button class="automation-toggle ${state.autoBook ? 'on' : ''}" data-automation="book"><span>AUTO-BOOK UNITS</span><small>${state.autoBook ? 'ON · books the top affordable tier each second' : 'OFF · book units by hand'}</small></button>` : ''}${respondUnlocked ? `<button class="automation-toggle ${state.autoRespond ? 'on' : ''}" data-automation="respond"><span>AUTO-RESPOND</span><small>${state.autoRespond ? 'ON · accepts optional command events' : 'OFF · respond by hand'}</small></button>` : ''}</div></div>`;
}

function signalMarkup() {
  if (!state.signal || cloudMode) return '';
  const remaining = Math.max(0, Math.ceil((state.signal.expiresAt - Date.now()) / 1000));
  const label = { frenzy: 'FRENZY', windfall: 'WINDFALL', breath: 'SECOND WIND', intel: 'LUCKY INTEL' }[state.signal.kind] || 'SIGNAL';
  return `<button id="catch-signal" class="rare-signal" title="Rare signal — catch it before it fades"><span class="signal-core">✦</span><b>RARE SIGNAL</b><small>${label} · ${remaining}s</small></button>`;
}

function seasonBannerMarkup() {
  const season = activeSeason();
  if (!season) return '';
  return `<div class="season-banner"><span>SEASONAL · ${season.name.toUpperCase()}</span> ${season.note} <strong>×${season.mult} OUTPUT</strong></div>`;
}

function tempoMetaMarkup() {
  const cost = tempoCost();
  const affordable = compare(state.credits, cost) >= 0;
  // A disabled button that says only its price reads as broken — the reported
  // symptom was "I click Operational Tempo and nothing happens", which is a
  // browser correctly swallowing clicks on a button you cannot afford. Say so.
  const eta = affordLabel(cost, false);
  const note = affordable
    ? 'READY'
    : `${format(subtract(cost, state.credits))} SHORT${eta === 'STAND BY' ? '' : ` · READY IN ${eta}`}`;
  return `<div class="meta-card"><div class="subpanel-heading"><span class="eyebrow">OPERATIONAL TEMPO</span><span class="panel-hint">×${tempoMultiplier().toFixed(2)} · LVL ${state.tempoLevel || 0}</span></div><p class="meta-note">Raise the operational tempo for a permanent +10% output — a home for late-game Credits.</p><button class="meta-buy meta-buy-lined" id="buy-tempo" ${affordable ? '' : 'disabled'}><span>RAISE TEMPO · ${format(cost)}</span><small>${note}</small></button></div>`;
}

function serviceRecordMarkup() {
  const count = (state.achievements || []).length;
  return `<div class="meta-card"><div class="subpanel-heading"><span class="eyebrow">SERVICE RECORD</span><span class="panel-hint">${count}/${ACHIEVEMENTS.length} · ×${serviceRecordMultiplier(count).toFixed(2)}</span></div><div class="badge-grid">${ACHIEVEMENTS.map((badge) => { const got = (state.achievements || []).includes(badge.id); return `<span class="badge ${got ? 'badge-on' : ''}" title="${badge.name}: ${badge.detail}">${got ? '★' : '·'}</span>`; }).join('')}</div></div>`;
}

function deploymentsMarkup() {
  if (!challengesUnlocked() && !(state.challengesDone || []).length && !state.activeChallenge) return '';
  const active = state.activeChallenge ? challengeFor(state.activeChallenge) : null;
  return `<div class="meta-card"><div class="subpanel-heading"><span class="eyebrow">DEPLOYMENTS</span><span class="panel-hint">${(state.challengesDone || []).length}/${CHALLENGES.length} DONE</span></div>${active
    ? `<div class="deploy-active"><strong>ON DEPLOYMENT · ${active.name}</strong><small>${active.detail}</small><small>Target: ${RANKS[active.target].short} · Reward: ${active.reward}</small><button class="meta-buy danger" id="abandon-challenge">ABANDON</button></div>`
    : `<div class="deploy-list">${CHALLENGES.map((challenge) => { const done = (state.challengesDone || []).includes(challenge.id); return `<button class="deploy-row ${done ? 'deploy-done' : ''}" data-challenge="${challenge.id}" ${done ? 'disabled' : ''}><strong>${challenge.name}${done ? ' ✓' : ''}</strong><small>${challenge.detail}</small><small>Reward: ${challenge.reward}</small></button>`; }).join('')}</div>`}</div>`;
}

function commissionMetaMarkup() {
  const unlocked = state.commendations >= COMMISSION_UNLOCK_COMMENDATIONS || (state.commissions || 0) > 0;
  if (!unlocked) return '';
  const gain = commandPointGain(state.commendations);
  return `<div class="meta-card"><div class="subpanel-heading"><span class="eyebrow">REGULAR COMMISSION</span><span class="panel-hint">${state.commandPoints || 0} CP · ${state.commissions || 0} SIGNED</span></div><p class="meta-note">Sign on as a Regular: wipe Commendations for Command Points and a permanent tree. ${canCommission() ? `<strong>+${gain} CP ready.</strong>` : `Need ${COMMISSION_UNLOCK_COMMENDATIONS} Commendations (have ${state.commendations}).`}</p><button class="meta-buy" id="open-commission" ${canCommission() ? '' : 'disabled'}>SIGN ON · +${gain} CP</button><div class="commission-tree">${COMMISSION_UPGRADES.map((upgrade) => { const owned = hasCommission(upgrade.id); const locked = upgrade.requires && !hasCommission(upgrade.requires); const afford = (state.commandPoints || 0) >= upgrade.cost; return `<button class="commission-node ${owned ? 'node-on' : ''}" data-commission="${upgrade.id}" ${owned || locked || !afford ? 'disabled' : ''} title="${upgrade.detail}"><strong>${upgrade.name}</strong><small>${owned ? 'ADOPTED' : locked ? `Needs ${commissionFor(upgrade.requires).name}` : `${upgrade.cost} CP`}</small></button>`; }).join('')}</div></div>`;
}

function sparklineSVG(points) {
  if (!points || points.length < 2) return '<div class="market-spark-empty">gathering ticks…</div>';
  const width = 220;
  const height = 46;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const step = width / (points.length - 1);
  const coords = points.map((price, index) => `${(index * step).toFixed(1)},${(height - ((price - min) / span) * (height - 8) - 4).toFixed(1)}`).join(' ');
  const up = points[points.length - 1] >= points[0];
  return `<svg class="market-spark" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none"><polyline points="${coords}" fill="none" stroke="${up ? '#76e0c9' : '#ed9283'}" stroke-width="1.5"/></svg>`;
}

function stockpileMarkup() {
  if (!state.pillars.includes('economic')) return '';
  const price = state.marketPrice || MARKET_BASE;
  const lots = state.marketLots || 0;
  const history = marketHistory();
  const prev = history.length > 1 ? history[history.length - 2] : price;
  const trend = price > prev ? '▲' : price < prev ? '▼' : '·';
  const buyDisabled = compare(state.credits, fromNumber(price * (1 + MARKET_FEE))) < 0;
  return `<div class="meta-card"><div class="subpanel-heading"><span class="eyebrow">STOCKPILE MARKET · ECON</span><span class="panel-hint market-${price >= prev ? 'up' : 'down'}">${trend} ${format(fromNumber(price))}</span></div>${sparklineSVG(history)}<div class="market-stats"><span>HOLDING ${format(fromNumber(lots))} LOTS</span><span>VALUE ${format(fromNumber(lots * price))}</span></div><div class="market-actions"><button class="meta-buy" data-market-buy="1" ${buyDisabled ? 'disabled' : ''}>BUY 1</button><button class="meta-buy" data-market-buy="10" ${buyDisabled ? 'disabled' : ''}>BUY 10</button><button class="meta-buy" data-market-sell="all" ${lots > 0 ? '' : 'disabled'}>SELL ALL</button></div><p class="meta-note">Buy low, sell high. 3% desk fee each side; the next move is anyone's guess.</p></div>`;
}

function advancementMarkup() {
  const cards = [tempoMetaMarkup(), deploymentsMarkup(), commissionMetaMarkup(), serviceRecordMarkup(), stockpileMarkup()].filter(Boolean).join('');
  return `<details class="panel advancement-panel"><summary class="arsenal-header"><div><p class="eyebrow">ADVANCEMENT</p><h2>Progression &amp; standing orders</h2></div><div class="arsenal-summary"><span>${(state.achievements || []).length} BADGES</span><span>${state.commandPoints || 0} CP</span></div></summary><div class="advancement-body">${cards}</div></details>`;
}

function breakdownMarkup() {
  const { base, factors, total } = multiplierBreakdown();
  const rows = [`<div class="breakdown-row"><span>Unit output</span><strong>${format(base)}/s</strong></div>`]
    .concat(factors.map((entry) => `<div class="breakdown-row"><span>${entry.label}${entry.note ? ` · ${entry.note}` : ''}</span><strong>×${entry.factor.toFixed(2)}</strong></div>`))
    .join('');
  return `<details class="breakdown"><summary><span class="eyebrow">OUTPUT BREAKDOWN</span><strong>${format(total)}/s</strong></summary><div class="breakdown-list">${rows}</div></details>`;
}

function commandMarkup() {
  const order = currentCommand();
  const target = order.targetFor(order.round);
  const progress = order.progressFor(state);
  const ready = progress >= target;
  const reward = fromNumber(order.rewardFor(order.round));
  return `<section class="command-order ${ready ? 'order-ready' : ''}"><div class="subpanel-heading"><span class="eyebrow">FIELD ORDER ${String(order.index + 1).padStart(2, '0')}</span><span class="panel-hint">${cloudMode ? 'SHARED WATCH' : 'LOCAL WATCH'}</span></div><h3>${order.label}</h3><p>${order.description}</p><div class="order-progress"><div><span>${order.progressLabel}</span><strong>${Math.min(progress, target)} / ${target}</strong></div><div class="order-meter"><span style="width:${Math.min(100, (progress / target) * 100)}%"></span></div></div><div class="order-reward"><span>REWARD · ${format(reward)} CREDITS + ${order.glimmerFor(order.round)} GLIMMER</span><button id="claim-command" ${ready ? '' : 'disabled'}>${ready ? 'CLAIM ORDER' : 'IN PROGRESS'}</button></div></section>`;
}

function upgradeMarkup() {
  return `<details class="upgrade-section"><summary><span><span class="eyebrow">UPGRADE DOCTRINE</span><strong>${state.upgrades.length} PURCHASED</strong></span><span class="panel-hint">THREE LINES</span></summary><div class="upgrade-list">${UPGRADES.map((upgrade) => { const owned = state.upgrades.includes(upgrade.id); const locked = state.rankIndex < upgrade.unlock || (upgrade.requires && !state.upgrades.includes(upgrade.requires)); const cost = fromNumber(upgrade.cost); return `<article class="upgrade-row ${owned ? 'upgrade-owned' : ''} ${locked ? 'upgrade-locked' : ''}"><div class="upgrade-line">${upgrade.line}</div><div><strong>${upgrade.name}</strong><small>${upgrade.detail}${upgrade.requires && locked ? ` · Requires ${upgrade.requires}` : ''}</small></div><button data-buy-upgrade="${upgrade.id}" ${owned || locked || compare(state.credits, cost) < 0 ? 'disabled' : ''}>${owned ? 'ADOPTED' : locked ? `RANK ${RANKS[upgrade.unlock].short}` : format(cost)}</button></article>`; }).join('')}</div></details>`;
}

function overlayMarkup() {
  if (!state.tutorialComplete) {
    const steps = [
      ['REPORT TO TEKONG', 'The island is quiet, the stores are not, and someone has already asked you to fall in. This is BMT: a short shared start before your vocation is assigned.', 'START BMT'],
      ['STAND-BY-BED', 'Area cleaning, guard duty, stores. The regulars establish the baseline while you learn where the buttons are.', 'COMPLETE BMT'],
      ['VOCATION STREAM', 'You are being streamed. Pick the service whose verb sounds most like your kind of waiting. You can appeal after ORD.', 'CONFIRM ASSIGNMENT'],
    ];
    const [title, copy, action] = steps[state.tutorialStep];
    return `<div class="overlay-backdrop"><section class="celebration-overlay tutorial-overlay"><span class="eyebrow">BMT / DAY ${state.tutorialStep + 1} OF 3</span><h2>${title}</h2><p>${copy}</p>${state.tutorialStep === 2 ? `<div class="tutorial-vocations">${VOCATIONS.map((vocation) => `<button class="vocation-option ${vocation.id === state.vocation ? 'selected-vocation' : ''}" data-tutorial-vocation="${vocation.id}"><span class="vocation-short">${vocation.short}</span><strong>${vocation.verb}</strong><small>${vocation.description}</small></button>`).join('')}</div>` : ''}<div class="overlay-actions"><button data-overlay="tutorial-next">${action}</button></div></section></div>`;
  }
  if (state.pendingOverlay === 'ord') return `<div class="overlay-backdrop"><section class="celebration-overlay ord-overlay"><span class="eyebrow">BOOK OUT / ORD COMPLETE</span><h2>The island keeps your name on the roster.</h2><p>You have reached ${RANKS[state.rankIndex].name}. This enlistment grants <strong>${ordGain()} Commendations</strong>, then returns you to the start with a permanent record and a little more weight behind every unit.</p><div class="ord-keeps"><span>+${Math.round(state.commendations * 1)}% BASE OUTPUT AFTER COMMENDATIONS</span><span>RANK RECORD · ${RANKS[state.rankRecord].short}</span><span>ICT / IPPT / PILLARS RESET FOR THE NEW ENLISTMENT</span></div><div class="overlay-actions"><button data-overlay="cancel">STAY ON WATCH</button><button class="primary-action" data-overlay="confirm-ord">ORD / BOOK OUT</button></div></section></div>`;
  if (state.pendingOverlay === 'commission') {
    const gain = commandPointGain(state.commendations);
    return `<div class="overlay-backdrop"><section class="celebration-overlay commission-overlay"><span class="eyebrow">SIGN ON / REGULAR COMMISSION</span><h2>You sign on as a Regular.</h2><p>This wipes your ${state.commendations} Commendations for <strong>${gain} Command Points</strong> and starts a fresh enlistment — but the Commission tree, your Service Record, and everything unlocked stays yours, and every run after this is faster.</p><div class="ord-keeps"><span>+${gain} COMMAND POINTS</span><span>${state.commissions || 0} COMMISSIONS SIGNED</span><span>COMMENDATIONS RESET</span></div><div class="overlay-actions"><button data-overlay="cancel">NOT YET</button><button class="primary-action" data-overlay="confirm-commission">SIGN ON</button></div></section></div>`;
  }
  if (state.pendingOverlay === 'pillar') {
    const pillar = PILLARS.find((entry) => entry.id === state.pendingPillarId) || PILLARS[0];
    return `<div class="overlay-backdrop"><section class="celebration-overlay pillar-overlay"><span class="eyebrow">TOTAL DEFENCE / ${pillar.short} PILLAR ONLINE</span><h2>${pillar.name} comes online.</h2><p>${pillar.description} The watch is no longer only military — new kinds of situation now answer to you, and the earlier pillars keep mattering.</p><div class="ord-keeps"><span>${state.pillars.length}/6 PILLARS ACTIVE</span><span>RANK · ${RANKS[state.rankIndex].short}</span><span>NEW WATCH TYPES IN ROTATION</span></div><div class="overlay-actions"><button class="primary-action" data-overlay="cancel">ACKNOWLEDGE</button></div></section></div>`;
  }
  if (state.pendingOverlay === 'ippt') return `<div class="overlay-backdrop"><section class="celebration-overlay ippt-overlay"><span class="eyebrow">OPTIONAL CHECK-IN / IPPT</span><h2>Three stations. No punishment for taking it easy.</h2><p>Tap through a short, forgiving check-in, then choose the result that feels right. The bonus is temporary; your progression is not.</p><div class="ippt-steps">${['PUSH-UPS', 'SIT-UPS', '2.4KM'].map((label, index) => `<button class="ippt-step ${index < state.ipptSteps ? 'step-done' : ''}" data-ippt-step="${index}" ${index !== state.ipptSteps ? 'disabled' : ''}><span>${index < state.ipptSteps ? '✓' : String(index + 1).padStart(2, '0')}</span><strong>${label}</strong><small>${index < state.ipptSteps ? 'LOGGED' : index === state.ipptSteps ? 'TAP TO COMPLETE' : 'STAND BY'}</small></button>`).join('')}</div>${state.ipptSteps >= 3 ? `<div class="grade-picker"><span class="eyebrow">SELECT YOUR RESULT</span><div>${[['pass', '+5%'], ['incentive', '+10%'], ['silver', '+15%'], ['gold', '+25%']].map(([grade, bonus]) => `<button data-ippt-grade="${grade}">${grade.toUpperCase()} <small>${bonus}</small></button>`).join('')}</div></div>` : ''}<div class="overlay-actions"><button data-overlay="cancel">BOOK OUT</button></div></section></div>`;
  return '';
}

// ---- Rally: the active verb ------------------------------------------------
// Tapping the theatre converts a slice of your standing output into an
// immediate payout, and chaining taps grows that slice. It is capped and
// derived from `rate()`, so it never becomes a better strategy than building
// the formation — it just makes being present feel like playing.
function rally(clientX, clientY) {
  advance();
  const now = Date.now();
  rallyChain = now < rallyChainUntil ? Math.min(RALLY_CHAIN_CAP, rallyChain + 1) : 1;
  rallyChainUntil = now + RALLY_CHAIN_WINDOW;
  const multiplier = 1 + (rallyChain - 1) * 0.12;
  const gain = multiply(rate(), RALLY_SECONDS * multiplier);
  state.credits = add(state.credits, gain);
  state.lifetime = add(state.lifetime, gain);
  state.rallies = Number(state.rallies || 0) + 1;
  const tone = rallyChain >= 12 ? 'amber' : 'cyan';
  Fx.floatText(clientX, clientY - 8, `+${format(gain)}${rallyChain > 1 ? `  ×${rallyChain}` : ''}`, { tone, size: Math.min(1.8, 0.95 + rallyChain * 0.05), drift: (Math.random() - 0.5) * 40 });
  Fx.ring(clientX, clientY, { tone, size: 60 + rallyChain * 4 });
  Fx.burst(clientX, clientY, { count: Math.min(16, 4 + rallyChain), tone, spread: 40 + rallyChain * 3 });
  if (rallyChain === RALLY_CHAIN_CAP) { Fx.flash('amber', 0.18); Fx.shake(5); }
  saveState();
}

// ---- Promotion celebration -------------------------------------------------
// advance() can promote you at any moment; the feed line alone made the single
// biggest progression beat in the game invisible. Watch for the change here and
// give it a proper moment.
function celebrateRank() {
  if (state.rankIndex <= lastRankSeen) { lastRankSeen = state.rankIndex; return; }
  lastRankSeen = state.rankIndex;
  Fx.banner(RANKS[state.rankIndex].name.toUpperCase(), 'PROMOTION LOGGED', 'amber');
  Fx.flash('amber', 0.26);
  Fx.shake(6);
}

// ---- Game modes ------------------------------------------------------------
function modeRoot() { return document.querySelector('#mode-root'); }

function modeContext(mode) {
  return {
    // Modes pay out in seconds of your standing output, converted here so the
    // mode files never need to know about BigNumber or the save shape.
    commit({ seconds, glimmer, label }) {
      advance();
      const payout = multiply(rate(), seconds);
      state.credits = add(state.credits, payout);
      state.lifetime = add(state.lifetime, payout);
      state.glimmer += glimmer;
      state.modeRuns = Number(state.modeRuns || 0) + 1;
      addFeed(`${label}. +${format(payout)} Credits and +${glimmer} Glimmer banked.`);
      saveState();
    },
    recordBest(score) {
      const bests = { ...(state.modeBests || {}) };
      const previous = Number(bests[mode.id] || 0);
      if (score > previous) {
        bests[mode.id] = score;
        state.modeBests = bests;
        saveState();
        Fx.banner('NEW PERSONAL BEST', `${mode.name.toUpperCase()} · ${score}`, 'amber');
      }
      return Math.max(previous, score);
    },
    exit: () => closeMode(),
  };
}

function startMode(id) {
  const mode = modeFor(id);
  if (!mode) return;
  closeMode(true);
  activeModeId = id;
  activeMode = mode.create(modeContext(mode));
  document.body.classList.add('mode-active');
  activeMode.mount(modeRoot());
}

function closeMode(silent = false) {
  activeMode?.destroy();
  activeMode = null;
  activeModeId = null;
  const root = modeRoot();
  if (root) root.innerHTML = '';
  if (silent) return;
  document.body.classList.remove('mode-active');
  render();
}

function modeHubMarkup() {
  const bests = state.modeBests || {};
  return `<section class="panel mode-hub">
    <div class="hub-heading">
      <div><p class="eyebrow">OPERATIONS ROOM</p><h2>Play a round</h2></div>
      <span class="panel-hint">${state.modeRuns || 0} RUNS LOGGED</span>
    </div>
    <div class="hub-grid">${MODES.map((mode) => `<button class="hub-card hub-${mode.id}" data-start-mode="${mode.id}">
      <span class="hub-kicker">${mode.kicker}</span>
      <strong>${mode.name}</strong>
      <em>${mode.tagline}</em>
      <small>${mode.description}</small>
      <span class="hub-foot"><i>BEST</i><b>${bests[mode.id] ? bests[mode.id] : '—'}</b><u>PLAY ▸</u></span>
    </button>`).join('')}</div>
  </section>`;
}

function render() {
  advance();
  celebrateRank();
  const currentRank = RANKS[state.rankIndex];
  const progress = rankProgress();
  const committed = totalPosture();
  const output = rate();
  const operation = currentOperation();
  const vocation = vocationFor(state.vocation);
  const commons = serviceMultipliers();
  const phase = phaseFor(state.rankIndex);
  const focused = SECTORS.find((sector) => sector.id === selectedSectorId) || SECTORS[5];
  const focusedPosture = Number(state.posture[focused.id] || 0);
  const surgeActive = state.surgeStartedAt && Date.now() < state.surgeUntil;
  const breathReady = !surgeActive && state.breath >= BREATH_MIN_S;
  const breathLabel = timeLabel(state.breath * 1000);
  const surgeState = {
    active: surgeActive,
    ready: breathReady,
    title: surgeActive ? `SURGE ACTIVE · ${Math.ceil((state.surgeUntil - Date.now()) / 1000)}s` : breathReady ? 'RELEASE BREATH' : 'BANKING BREATH',
    note: surgeActive ? `Output ×${SURGE_MULT} · living theatre lit` : breathReady ? `${breathLabel} banked → ${Math.round(surgeDurationFor(state.breath) / 1000)}s burst` : `Time away banks a burst · ${breathLabel} banked`,
  };
  const nextEvent = state.currentEvent ? operationFor(state.currentEvent) : operation;
  const worldTime = clock.label();
  const accountLabel = !auth.configured ? 'ACCOUNTS OFFLINE' : auth.session ? 'SHARED IDENTITY' : 'GUEST · LINK ACCOUNT';
  sceneSnapshot = { ...state, activeEvent: state.currentEvent ? { ...state.currentEvent, operation: operationFor(state.currentEvent) } : null, eventWindowRemaining: state.currentEvent ? Math.max(0, state.currentEvent.resolvesAt - clock.now()) : 0, sectorCoords: SECTOR_COORDS };

  const markup = `<div class="shell game-shell">
    <header class="topbar"><div class="brand-mark"><span class="brand-dot"></span><span>MELODIA / IDLER</span></div><div class="topbar-right"><span class="connection-status"><i></i> ${cloudMode ? 'CLOUD AUTHORITY LINK' : 'LOCAL COMMAND LINK'}</span><button id="account-button" class="account-button"><i></i>${accountLabel}</button><div class="build-tag">TOTAL DEFENCE <span>FIELD BUILD · M3</span></div></div></header>
    ${offlineReport ? `<div class="offline-report"><span>WELCOME BACK</span> The island kept moving for ${offlineReport.duration}; <strong>+${format(offlineReport.earned)} Credits</strong> added at the offline rate.${offlineReport.events?.length ? ` Watches resolved while away: ${offlineReport.events.join(', ')}.` : ''} <button id="dismiss-offline">ACKNOWLEDGE</button></div>` : ''}
    ${seasonBannerMarkup()}
    <main class="game-frame"><section class="panel theatre-panel"><div class="theatre-header"><div><p class="eyebrow">NATIONAL DEFENCE NETWORK · ${phase.name} WATCH</p><h1>Watch the island breathe.</h1><p class="theatre-lede">Tap the island to rally — every tap pays out, and tapping in a chain pays more. It keeps its own quiet watch while you are away.</p></div><div class="theatre-clock"><span class="live-pip"></span><strong>${currentRank.short} · ${vocation.short}</strong><small>${worldTime} · ${clock.dayPhase().night ? 'NIGHT SHIFT' : 'DAY SHIFT'}</small></div></div>
      <div class="map-stage" id="rally-stage"><canvas id="sector-canvas" data-static aria-label="Animated illustrated island theatre"></canvas><div class="rally-chain ${rallyChain > 1 && Date.now() < rallyChainUntil ? 'chain-live' : ''}"><b>×${rallyChain > 1 && Date.now() < rallyChainUntil ? rallyChain : 1}</b><small>RALLY CHAIN</small></div><div class="map-scanlines"></div><div class="map-atmosphere"><span>REAL-TIME ISLAND VIEW</span><span>01° 21′ N · 103° 49′ E</span></div><div class="map-label map-label-north">NORTH / CONTINENTAL APPROACH</div><div class="map-label map-label-south">SOUTH / SHIPPING LANES</div><div class="map-compass">N<br><span>+</span></div><div class="sector-nodes">${SECTORS.map((sector) => sectorNodeMarkup(sector, nextEvent)).join('')}</div>${signalMarkup()}<div class="map-footer"><span><i class="legend-light"></i> LIVING INFRASTRUCTURE</span><span><i class="legend-army"></i> RESPONSE ROUTES</span><span>${committed}/100 POSTURE · MAX ${commons.maxActiveSectors} ACTIVE SECTORS</span></div></div>
      ${situationMarkup(nextEvent)}
    </section>
    <aside class="panel command-panel"><div class="panel-heading command-heading"><div><p class="eyebrow">COMMAND CONSOLE</p><h2>${vocation.short} / ${currentRank.short}</h2></div><span class="panel-code">LOCAL · M3</span></div><div class="console-status"><span class="status-dot quiet-dot"></span><div><strong>${vocation.name} · ${vocation.verb}</strong><small>${phase.flavour} ${state.isNsman ? 'The reservist file remains open.' : ''}</small></div></div><div class="credit-display"><span class="stat-label">FIELD CREDITS</span><strong>${format(state.credits)}</strong><div><span>${format(output)} /s OUTPUT</span><span>${format(state.lifetime)} LIFETIME</span></div><div class="commons-line"><span>${vocation.commonsLabel} ${format(state.commons[vocation.commons])}</span><span>${commons.supply.toFixed(2)}× SUPPLY</span></div><div class="commons-line"><span>GLIMMER ${state.glimmer}</span><span>${state.archive.length} ARCHIVE ENTRIES</span></div></div>${breakdownMarkup()}<button id="surge-button" class="surge-button ${surgeState.active ? 'surge-live' : ''}" ${surgeState.ready ? '' : 'disabled'}><span class="surge-icon">✦</span><span><b>${surgeState.title}</b><small>${surgeState.note}</small></span><span class="surge-arrow">→</span></button>${lifecycleMarkup()}${commandMarkup()}${vocationMarkup()}<div class="focus-card"><div class="focus-top"><span class="eyebrow">FOCUS SECTOR</span><span class="focus-code">S-${String(focused.id).padStart(2, '0')}</span></div><h3>${focused.name}</h3><div class="focus-meta"><span>${focused.domain}</span><span>PRIMARY: ${focused.primary}</span></div><div class="focus-posture"><div><span>POSTURE</span><strong>${focusedPosture}%</strong></div><div class="focus-controls"><button data-adjust-posture="-10">−10</button><button data-adjust-posture="10">+10</button></div></div><div class="focus-meter"><span style="width:${focusedPosture}%"></span></div></div><div class="rank-block"><div class="rank-line"><span><b>${currentRank.name}</b> · PROMOTION TRACK</span><span>${progress.label}</span></div><div class="progress-track"><span style="width:${progress.percent}%"></span></div></div>${pillarMarkup()}<div class="log-heading"><span class="eyebrow">DUTY LOG</span><span>${String(feed.length).padStart(2, '0')} SIGNALS</span></div><div class="feed compact-feed">${feed.slice(0, 4).map((entry) => `<div class="feed-entry"><span class="feed-time">${entry.time}</span><p>${entry.text}</p></div>`).join('')}</div></aside></main>
    <details class="panel arsenal-panel"><summary class="arsenal-header"><div><p class="eyebrow">SECONDARY COMMAND PANEL</p><h2>${vocation.name} · force generation</h2></div><div class="arsenal-summary"><span>${committed}% POSTURE COMMITTED</span><span>OFFLINE RATE · ${offlineFactor() * 100}%</span></div></summary><div class="arsenal-body"><div class="unit-list">${activeUnits().map((unit) => unitMarkup(unit)).join('')}</div>${automationMarkup()}${upgradeMarkup()}<p class="panel-note"><span class="note-icon">i</span> Credits and units stay available here when you want to tune the formation. ${vocation.description}</p></div></details>
    ${modeHubMarkup()}
    ${advancementMarkup()}
    <footer class="footer"><span>LOCAL SAVE · AUTOSAVING</span><span>PEACETIME / LIVING WATCH</span><button id="reset-save">RESET PROTOTYPE SAVE</button></footer>
    ${overlayMarkup()}</div>`;

  // Patch in place rather than replacing #app. Transitions, keyframes, focus,
  // <details> state and the canvas all survive the once-a-second redraw.
  morphInto(document.querySelector('#app'), markup);

  // The scene is created once and outlives every redraw, reading whatever the
  // latest snapshot is rather than being handed a new closure each time.
  if (!scene) {
    scene = new WorldScene(document.querySelector('#sector-canvas'), { clock, getState: () => sceneSnapshot });
    scene.start();
  }
}

// One delegated listener for the whole command view. Because morph reuses
// nodes, per-render rebinding would stack duplicate handlers — delegation is
// both cheaper and the only correct option here.
function handleAppClick(event) {
  const hit = (selector) => event.target.closest(selector);
  const target = hit('[data-buy-tier]');
  if (target) {
    const before = Number(state.units[Number(target.dataset.buyTier)] || 0);
    buyUnit(Number(target.dataset.buyTier), target.dataset.buyCount === 'max' ? 'max' : Number(target.dataset.buyCount));
    const gained = Number(state.units[Number(target.dataset.buyTier)] || 0) - before;
    if (gained > 0) Fx.reward(target, `+${gained}`, 'cyan');
    return;
  }
  const sector = hit('[data-select-sector]');
  if (sector) { selectSector(Number(sector.dataset.selectSector)); return; }
  const posture = hit('[data-adjust-posture]');
  if (posture) { setPosture(selectedSectorId, Number(state.posture[selectedSectorId] || 0) + Number(posture.dataset.adjustPosture)); return; }
  const vocation = hit('[data-vocation]');
  if (vocation) { chooseVocation(vocation.dataset.vocation); return; }
  const upgrade = hit('[data-buy-upgrade]');
  if (upgrade) {
    const owned = state.upgrades.length;
    buyUpgrade(upgrade.dataset.buyUpgrade);
    if (state.upgrades.length > owned) { Fx.reward(upgrade, 'ADOPTED', 'amber'); Fx.shake(4); }
    return;
  }
  const lifecycle = hit('[data-lifecycle]');
  if (lifecycle) {
    ({ ict: startIct, ippt: openIppt, ord: openOrd })[lifecycle.dataset.lifecycle]?.();
    return;
  }
  if (hit('[data-ippt-step]')) { completeIpptStep(); return; }
  const grade = hit('[data-ippt-grade]');
  if (grade) { finishIppt(grade.dataset.ipptGrade); return; }
  const overlay = hit('[data-overlay]');
  if (overlay) {
    const action = overlay.dataset.overlay;
    if (action === 'cancel') { state.pendingOverlay = null; saveState(); render(); }
    else if (action === 'confirm-ord') { Fx.flash('amber', 0.4); Fx.shake(10); confirmOrd(); }
    else if (action === 'confirm-commission') { Fx.flash('amber', 0.4); Fx.shake(10); confirmCommission(); }
    else if (action === 'tutorial-next') { state.tutorialStep >= 2 ? completeTutorial() : advanceTutorial(); }
    return;
  }
  const tutorialVocation = hit('[data-tutorial-vocation]');
  if (tutorialVocation) { state.vocation = tutorialVocation.dataset.tutorialVocation; render(); return; }
  const automation = hit('[data-automation]');
  if (automation) {
    const key = automation.dataset.automation === 'book' ? 'autoBook' : 'autoRespond';
    const nextValue = !state[key];
    const label = `${automation.dataset.automation === 'book' ? 'Auto-book' : 'Auto-respond'} ${nextValue ? 'engaged' : 'stood down'}.`;
    if (cloudMode) {
      const book = key === 'autoBook' ? nextValue : state.autoBook;
      const respond = key === 'autoRespond' ? nextValue : state.autoRespond;
      syncCloudIntent(cloud.setAutomation(book, respond), label);
      return;
    }
    state[key] = nextValue;
    addFeed(label);
    saveState();
    render();
    return;
  }
  const marketBuy = hit('[data-market-buy]');
  if (marketBuy) { buyLots(Number(marketBuy.dataset.marketBuy)); return; }
  if (hit('[data-market-sell]')) { sellLots(state.marketLots || 0); return; }
  const manager = hit('[data-manager]');
  if (manager) { buyManager(Number(manager.dataset.manager)); Fx.reward(manager, 'HIRED', 'cyan'); return; }
  const challenge = hit('[data-challenge]');
  if (challenge) { startChallenge(challenge.dataset.challenge); return; }
  const commission = hit('[data-commission]');
  if (commission) { buyCommissionUpgrade(commission.dataset.commission); return; }
  const startModeButton = hit('[data-start-mode]');
  if (startModeButton) { startMode(startModeButton.dataset.startMode); return; }
  if (hit('#respond-event')) { respondToEvent(); Fx.flash('amber', 0.22); Fx.shake(6); return; }
  if (hit('#preempt-event')) { preemptEvent(); Fx.flash('cyan', 0.24); return; }
  const signalButton = hit('#catch-signal');
  if (signalButton) { Fx.reward(signalButton, 'CAUGHT', 'amber'); Fx.flash('amber', 0.3); Fx.shake(7); catchSignal(); return; }
  const tempo = hit('#buy-tempo');
  if (tempo) { const level = state.tempoLevel; buyTempo(); if (state.tempoLevel > level) Fx.reward(tempo, '+10%', 'cyan'); return; }
  if (hit('#open-commission')) { openCommission(); return; }
  if (hit('#abandon-challenge')) { abandonChallenge(); return; }
  const surge = hit('#surge-button');
  if (surge) { const was = state.surgeUntil; activateSurge(); if (state.surgeUntil > was) { Fx.flash('amber', 0.35); Fx.shake(9); Fx.banner('BREATH RELEASED', `OUTPUT ×${SURGE_MULT}`, 'amber'); } return; }
  const claim = hit('#claim-command');
  if (claim) { const index = state.commandIndex; claimCommand(); if (state.commandIndex > index) { Fx.reward(claim, 'ORDER COMPLETE', 'amber'); Fx.shake(5); } return; }
  if (hit('#account-button')) { accountUI?.open(); return; }
  if (hit('#dismiss-offline')) { offlineReport = null; render(); return; }
  if (hit('#reset-save')) {
    if (!window.confirm('Reset the local Total Defence prototype save?')) return;
    state = initialState();
    offlineReport = null;
    lastRankSeen = 0;
    feed = [{ time: 'now', text: 'Field account reopened. The regulars are already on duty.' }];
    saveState();
    render();
  }
}

// Rally taps land on the theatre itself, but must not steal clicks from the
// sector nodes, the rare signal, or anything else layered over the map.
function handleStagePointer(event) {
  const stage = event.target.closest('#rally-stage');
  if (!stage) return;
  if (event.target.closest('button, a, [data-select-sector]')) return;
  rally(event.clientX, event.clientY);
}

document.querySelector('#app').addEventListener('click', handleAppClick);
document.querySelector('#app').addEventListener('pointerdown', handleStagePointer);

accountUI = new AccountUI(document.querySelector('#account-root'), { auth, onSession: handleAuthSession });

const firstElapsed = Math.max(0, (Date.now() - state.lastTick) / 1000);
const historyBefore = state.eventHistory.length;
const firstEarned = advance(offlineFactor());
if (firstElapsed >= 60 && toNumber(firstEarned) > 0) {
  const minutes = Math.floor(firstElapsed / 60);
  const newEntries = state.eventHistory.length > historyBefore ? state.eventHistory.slice(0, state.eventHistory.length - historyBefore) : [];
  const tally = newEntries.reduce((acc, entry) => {
    const key = String(entry.outcome || '').split(';')[0].trim() || 'resolved';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  offlineReport = {
    earned: firstEarned,
    duration: minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`,
    events: Object.entries(tally).map(([outcome, quantity]) => `${quantity} ${outcome}`),
  };
}
lastRankSeen = state.rankIndex;
saveState();
render();
auth.restore().then(() => {
  accountUI.render();
  if (auth.session) {
    preserveGuestSave();
    syncCloudState();
  }
});

setInterval(() => {
  advance();
  marketTick();
  recordMarketPrice(state.marketPrice || MARKET_BASE);
  runAutomation();
  // Career Track: auto-ORD as soon as it would bank Commendations.
  if (!cloudMode && hasCommission('auto-ord') && state.rankIndex >= 10 && ordGain() > 0 && !state.pendingOverlay) {
    confirmOrd();
    return;
  }
  saveState();
  // While a mode has the screen, the watch keeps earning but there is nothing
  // behind the takeover worth repainting.
  if (!activeMode) render();
}, 1000);

setInterval(() => {
  if (cloudMode) syncCloudState();
}, 60_000);

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    advance(1);
    inBackground = true;
  } else {
    advance(offlineFactor());
    inBackground = false;
  }
  saveState();
  render();
});
